import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ClockIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  XMarkIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { workloadAPI, feedbackAPI, policiesAPI, leaveRequestsAPI, checkoutRequestsAPI, extraHoursRequestsAPI, attendanceCorrectionsAPI, deviceSecurityAPI } from '../services/api';
import moment from 'moment-timezone';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-toastify';
import CheckInWidget from '../components/CheckInWidget';
import NotificationBell from '../components/NotificationBell';
import { initPush } from '../utils/pushNotifications';
import '../styles/dashboard-animations.css';
import DashboardSwitcher from '../components/DashboardSwitcher';
import { executeRecaptcha } from '../utils/recaptcha';
import UserSelfProfileModal from '../components/UserSelfProfileModal';
import UserProfilePhotoUploadModal from '../components/UserProfilePhotoUploadModal';
import useNativePullToRefresh from '../hooks/useNativePullToRefresh';
import { getOrCreateMobileDeviceId } from '../utils/mobileDeviceId';
import { calculateCheckoutEligibleDates } from './userDashboard/calculateCheckoutEligibleDates';
import { isBeforeTodayCheckoutRequestCutoff } from './userDashboard/checkoutRequestCutoff';
import { getCheckoutEligibilitySource } from './userDashboard/getCheckoutEligibilitySource';
import { getHolidayDetailItems } from './userDashboard/holidayDetailView';
import EmployeeDashboardStatsHub from './userDashboard/EmployeeDashboardStatsHub';
import EmployeeDashboardDetailSheet from './userDashboard/EmployeeDashboardDetailSheet';
import {
  DASHBOARD_SECTIONS,
  DEFAULT_PERFORMANCE_DAYS,
  getDefaultDashboardSection,
  getEmployeeDashboardStats,
  getHolidaysInMonth,
} from './userDashboard/dashboardStatsHub';
import { mergeDashboardUser } from './userDashboard/dashboardUser';

// Day counts can be fractional once Half Day leaves are involved (e.g. 4.5 working days).
// Show whole numbers plainly and fractions with a single decimal.
const formatDayCount = (value) => {
  const num = Number(value) || 0;
  return Number.isInteger(num) ? String(num) : num.toFixed(1);
};

function UserDashboard() {
  const navigate = useNavigate();
  const { isSuperAdmin, user: authUser, logout, deviceLock, setDeviceLock, deviceRegistration, updateUser } = useAuth();
  const [deviceChangePending, setDeviceChangePending] = useState(false);
  const [user, setUser] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showProfilePhotoUpload, setShowProfilePhotoUpload] = useState(false);
  const scrollPositionRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  
  // Prevent super admin from accessing user dashboard
  useEffect(() => {
    if (isSuperAdmin() && authUser?.email === 'admin@mastersunion.org') {
      window.location.href = '/';
    }
  }, [isSuperAdmin, authUser]);
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [customStartDate, setCustomStartDate] = useState(moment().subtract(30, 'days').toDate());
  const [customEndDate, setCustomEndDate] = useState(new Date());
  
  // Checkout request states
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [requestDate, setRequestDate] = useState(null);
  const [requestTime, setRequestTime] = useState('18:00');
  const [requestReason, setRequestReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [eligibleDates, setEligibleDates] = useState([]);
  const [unclosedCheckInDate, setUnclosedCheckInDate] = useState(null);
  
  // Leave request states
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [myLeaveRequests, setMyLeaveRequests] = useState([]);
  const [leaveStartDate, setLeaveStartDate] = useState(new Date());
  const [leaveEndDate, setLeaveEndDate] = useState(new Date());
  const [leaveType, setLeaveType] = useState('Casual Leave');
  const [leaveReason, setLeaveReason] = useState('');
  const [submittingLeave, setSubmittingLeave] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);

  // Limits state (approved-only, per department)
  const [limitsByMonth, setLimitsByMonth] = useState({});
  const [limitsLoading, setLimitsLoading] = useState(false);
  
  // Extra hours request states
  const [showExtraHoursModal, setShowExtraHoursModal] = useState(false);
  const [myExtraHoursRequests, setMyExtraHoursRequests] = useState([]);
  const [extraHoursDate, setExtraHoursDate] = useState(new Date());
  const [extraHoursRequested, setExtraHoursRequested] = useState(1);
  const [extraHoursReason, setExtraHoursReason] = useState('');
  const [extraHoursStartTime, setExtraHoursStartTime] = useState(null);
  const [extraHoursEndTime, setExtraHoursEndTime] = useState(null);
  const [submittingExtraHours, setSubmittingExtraHours] = useState(false);

  // Attendance calendar + correction request states
  const [calendarMonth, setCalendarMonth] = useState(moment().startOf('month'));
  const [calendarData, setCalendarData] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [myCorrectionRequests, setMyCorrectionRequests] = useState([]);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionDate, setCorrectionDate] = useState(null);
  const [correctionCheckInTime, setCorrectionCheckInTime] = useState('09:00');
  const [correctionCheckOutTime, setCorrectionCheckOutTime] = useState('18:00');
  const [correctionReason, setCorrectionReason] = useState('');
  const [submittingCorrection, setSubmittingCorrection] = useState(false);
  const [checkoutRedirectWarning, setCheckoutRedirectWarning] = useState(null);
  
  // ClickUp task states
  const [myTasks, setMyTasks] = useState([]);
  const [clickupLoading, setClickupLoading] = useState(false);
  const [showTasksSection, setShowTasksSection] = useState(false);
  
  // Performance metrics states
  const [performanceData, setPerformanceData] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceDays, setPerformanceDays] = useState(DEFAULT_PERFORMANCE_DAYS);

  // Workload timeline states
  const [workloadData, setWorkloadData] = useState(null);
  const [workloadLoading, setWorkloadLoading] = useState(false);
  const [timelineStartDate, setTimelineStartDate] = useState(moment().startOf('week').toDate());
  const [timelineDays, setTimelineDays] = useState(14);
  const [showWorkloadSection, setShowWorkloadSection] = useState(true);
  const [editingTask, setEditingTask] = useState(null);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [taskEditForm, setTaskEditForm] = useState({ taskId: '', start_date: '', due_date: '' });
  const [savingTask, setSavingTask] = useState(false);
  const [expandedDates, setExpandedDates] = useState(new Set()); // Track which dates show all tasks
  
  // Feedback unread replies count
  const [unreadRepliesCount, setUnreadRepliesCount] = useState(0);
  const [checkInWidgetRefreshToken, setCheckInWidgetRefreshToken] = useState(0);
  const [activeDashboardSection, setActiveDashboardSection] = useState(getDefaultDashboardSection());
  const [showAllHolidayDetails, setShowAllHolidayDetails] = useState(false);

  const pullToRefresh = useNativePullToRefresh({
    disabled: loading || updating || !user,
    onRefresh: async () => {
      if (!user) {
        return;
      }

      setError(null);
      setUpdating(true);

      await Promise.all([
        loadAttendanceData(user),
        fetchUnreadRepliesCount(),
      ]);
      setCheckInWidgetRefreshToken((current) => current + 1);
    },
  });

  const leaveLimitTypes = [
    'Casual Leave',
    'Sick Leave',
    'Earned Leave',
    'Emergency Leave',
    'Work from Home',
    'Half Day',
    'Short Leave'
  ];

  const recentAttendanceLogs = useMemo(() => {
    if (!attendanceData?.dailyHours?.length) {
      return [];
    }

    const logs = [];
    attendanceData.dailyHours.forEach((day) => {
      if (day.firstCheckIn) {
        logs.push({
          logTime: day.firstCheckIn,
          punchLogType: 'Check In'
        });
      }
      if (day.lastCheckOut) {
        logs.push({
          logTime: day.lastCheckOut,
          punchLogType: 'Check Out'
        });
      }
    });

    return logs
      .filter((log) => log.logTime)
      .sort((a, b) => new Date(b.logTime) - new Date(a.logTime))
      .slice(0, 8);
  }, [attendanceData?.dailyHours]);

  const currentMonthHolidays = useMemo(
    () => getHolidaysInMonth(upcomingHolidays, moment()),
    [upcomingHolidays]
  );

  const dashboardStats = useMemo(() => getEmployeeDashboardStats({
    attendanceData,
    myRequests,
    myLeaveRequests,
    myExtraHoursRequests,
    myTasks,
    upcomingHolidays: currentMonthHolidays,
    currentMonth: moment(),
    performanceData,
  }), [
    attendanceData,
    myRequests,
    myLeaveRequests,
    myExtraHoursRequests,
    myTasks,
    currentMonthHolidays,
    performanceData,
  ]);


  // Fetch unread replies count
  const fetchUnreadRepliesCount = async () => {
    try {
      const response = await feedbackAPI.getUnreadRepliesCount();
      setUnreadRepliesCount(response.data.unreadCount || 0);
    } catch (error) {
      // Silently fail - not critical
      console.error('Error fetching unread replies count:', error);
      setUnreadRepliesCount(0);
    }
  };

  // Initialization effect - runs only once on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/login', { replace: true });
      return;
    }

    let interval = null;
    
    try {
      const parsedUser = JSON.parse(userData);
      
      // Super admin (admin@mastersunion.org) should never access user dashboard
      if (parsedUser.isSuperAdmin && parsedUser.email === 'admin@mastersunion.org') {
        window.location.href = '/';
        return;
      }
      
      // ProtectedRoute already ensures user has role === 'user', so we can proceed
      // No need to check role again here as it causes redirect issues
      setUser(parsedUser);
      loadAttendanceData(parsedUser);
      fetchUnreadRepliesCount();
      loadHolidays();
      
      // Initialize push notifications for mobile devices
      initPush(token);
      
      // Refresh unread count every 30 seconds
      interval = setInterval(() => {
        fetchUnreadRepliesCount();
      }, 30000);
    } catch (err) {
      console.error('Error parsing user data:', err);
      navigate('/login', { replace: true });
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Separate effect to reload attendance data when period/date changes
  useEffect(() => {
    if (user) {
      // Preserve scroll position when updating period (not initial load)
      if (!isInitialLoadRef.current) {
        scrollPositionRef.current = window.scrollY;
        setUpdating(true); // Show inline loader for period updates
      } else {
        setLoading(true); // Show full-page loader only on initial load
      }
      loadAttendanceData(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, customStartDate, customEndDate]);

  // Restore scroll position after data loads (for period updates)
  useEffect(() => {
    if (!loading && !updating && !isInitialLoadRef.current && scrollPositionRef.current > 0) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        window.scrollTo(0, scrollPositionRef.current);
      }, 0);
    }
    if (loading === false && isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
    }
  }, [loading, updating]);

  const loadAttendanceData = async (userData) => {
    try {
      const token = localStorage.getItem('token');
      
      let startDate, endDate;
      switch (selectedPeriod) {
        case 'week':
          startDate = moment().startOf('isoWeek').format('YYYY-MM-DD'); // Monday
          endDate = moment().endOf('isoWeek').format('YYYY-MM-DD');     // Sunday
          break;
        case 'month':
          startDate = moment().startOf('month').format('YYYY-MM-DD');
          endDate = moment().endOf('month').format('YYYY-MM-DD');
          break;
        case 'year':
          startDate = moment().startOf('year').format('YYYY-MM-DD');
          endDate = moment().endOf('year').format('YYYY-MM-DD');
          break;
        case 'custom':
          startDate = moment(customStartDate).format('YYYY-MM-DD');
          endDate = moment(customEndDate).format('YYYY-MM-DD');
          break;
        default:
          startDate = moment().startOf('month').format('YYYY-MM-DD');
          endDate = moment().endOf('month').format('YYYY-MM-DD');
      }

      const response = await axios.get('/api/user/summary', {
        params: { startDate, endDate },
        headers: { Authorization: `Bearer ${token}` }
      });

      setAttendanceData(response.data);
      const refreshedUser = mergeDashboardUser(userData, response.data?.user);
      setUser(refreshedUser);
      if (response.data?.user?.profilePhotoUrl) {
        updateUser?.(response.data.user);
      }

      // Load limits for current month (monthly usage + annual totals)
      await loadLimitsForMonth(moment().format('YYYY-MM'), { force: true });
      
      // Load checkout requests with fresh attendance context
      await loadMyRequests(response.data);
      
      // Load leave requests
      loadMyLeaveRequests();
      
      // Load extra hours requests
      loadMyExtraHoursRequests();
      
      // Load ClickUp tasks
      loadMyClickUpTasks(userData);
      
      // Load performance metrics
      loadMyPerformance(userData);
      
      // Load upcoming holidays (use employment type from response or userData)
      const empType = response.data?.user?.employmentType || userData?.employementType || 'Full-Time';
      loadUpcomingHolidays(empType);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      } else {
        setError(err.response?.data?.error || 'Failed to load attendance data');
      }
    } finally {
      setLoading(false);
      setUpdating(false);
    }
  };

  const handleProfileSaved = (updatedUser) => {
    if (!updatedUser) return;
    const mergedUser = {
      ...(user || authUser || {}),
      ...updatedUser,
    };
    setUser(mergedUser);
    updateUser?.(updatedUser);
  };

  const refreshCheckInWidget = () => {
    setCheckInWidgetRefreshToken((current) => current + 1);
  };

  const handleRequestDeviceChange = async () => {
    try {
      const deviceId = await getOrCreateMobileDeviceId();
      if (!deviceId) {
        toast.error('Device identity is not available on this session.');
        return;
      }

      await deviceSecurityAPI.requestDeviceChange({ deviceId });
      setDeviceChangePending(true);
      toast.success('Device change request sent to your manager and super admin.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send device change request');
    }
  };

  const handleRequestDeviceUnlock = async () => {
    try {
      const deviceId = await getOrCreateMobileDeviceId();
      if (!deviceId) {
        toast.error('Device identity is not available on this session.');
        return;
      }

      const response = await deviceSecurityAPI.requestUnlock({
        deviceId
      });

      const pendingRequest = response.data.request;
      setDeviceLock((current) => ({
        ...(current || {}),
        pendingRequest
      }));
      toast.success('Device unlock request sent to your manager and super admin.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send device unlock request');
    }
  };

  const loadMyClickUpTasks = async (userData) => {
    try {
      setClickupLoading(true);
      const token = localStorage.getItem('token');
      
      // Get email from userData parameter or user state
      const userEmail = userData?.email || user?.email || user?.officialEmail;
      
      if (!userEmail) {
        console.log('⚠️ No user email found in:', userData);
        console.log('   User state:', user);
        return;
      }

      console.log('📋 Fetching my tasks for:', userEmail);
      
      // Try ClickUp API first (prioritize ClickUp when configured)
      let response = null;
      try {
        response = await axios.get(`/api/clickup/user/${userEmail}/tasks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('📥 ClickUp response:', response.data);
      } catch (clickupErr) {
        // If ClickUp fails (not configured or error), try Orbit API
        console.log('⚠️ ClickUp API failed or not configured, trying Orbit API...', clickupErr.response?.data?.error || clickupErr.message);
        try {
          response = await axios.get(`/api/orbit/user/${userEmail}/tasks`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('📥 Orbit response:', response.data);
        } catch (orbitErr) {
          // Both failed, silently fail as task integration is optional
          console.log('❌ Both ClickUp and Orbit tasks failed (optional):', orbitErr.response?.data || orbitErr.message);
          return;
        }
      }

      if (response && response.data.success) {
        console.log('✅ My tasks loaded:', response.data.taskCount);
        setMyTasks(response.data.tasks || []);
        // Don't auto-show - let user click to view
      }
    } catch (err) {
      // Task integration is optional, silently fail
      console.log('❌ Task integration error:', err.response?.data || err.message);
      console.error('Full error:', err);
    } finally {
      setClickupLoading(false);
    }
  };

  const loadMyPerformance = async (userData) => {
    try {
      setPerformanceLoading(true);
      const token = localStorage.getItem('token');
      const userId = userData?.id || user?.id;
      
      if (!userId) {
        console.log('⚠️ No user ID found for performance');
        return;
      }

      console.log('📊 Loading performance for user:', userId);
      const response = await axios.get(`/api/clickup/performance/user/${userId}`, {
        params: { days: performanceDays },
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('✅ Performance data:', response.data);

      if (response.data.success) {
        setPerformanceData(response.data);
      }
    } catch (err) {
      console.log('❌ Performance error:', err.response?.data || err.message);
    } finally {
      setPerformanceLoading(false);
    }
  };

  // Reload performance when days change
  useEffect(() => {
    if (user) {
      loadMyPerformance(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performanceDays]);

  const loadWorkloadData = async () => {
    try {
      setWorkloadLoading(true);
      
      // Try ClickUp API first (prioritize ClickUp when configured)
      let response = null;
      try {
        response = await workloadAPI.getMyWorkload();
        if (response.data && response.data.success) {
          console.log('✅ ClickUp workload data loaded');
          setWorkloadData(response.data);
          toast.success('Workload timeline loaded!');
          return;
        } else if (response.data && response.data.error) {
          // If ClickUp has error, try Orbit as fallback
          throw new Error(response.data.error);
        }
      } catch (clickupErr) {
        // If ClickUp fails, try Orbit API
        console.log('⚠️ ClickUp workload failed or not configured, trying Orbit...', clickupErr.message);
        try {
          response = await workloadAPI.getMyWorkloadOrbit();
          if (response.data && response.data.success) {
            console.log('✅ Orbit workload data loaded');
            setWorkloadData(response.data);
            toast.success('Workload timeline loaded!');
          } else {
            if (response.data && response.data.error) {
              toast.info(response.data.error);
            }
            setWorkloadData(response.data || {});
          }
        } catch (orbitErr) {
          console.error('❌ Both ClickUp and Orbit workload failed:', orbitErr.message);
          toast.error('Failed to load workload data');
        }
      }
    } catch (err) {
      console.error('Failed to load workload data:', err);
      toast.error('Failed to load workload data');
    } finally {
      setWorkloadLoading(false);
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskEditForm({
      taskId: task.id,
      start_date: task.start_date ? moment(parseInt(task.start_date)).format('YYYY-MM-DD') : '',
      due_date: task.due_date ? moment(parseInt(task.due_date)).format('YYYY-MM-DD') : ''
    });
    setShowEditTaskModal(true);
  };

  const handleSaveTask = async () => {
    if (!taskEditForm.start_date || !taskEditForm.due_date) {
      toast.error('Please provide both start and due dates');
      return;
    }

    try {
      setSavingTask(true);
      const start_date_ts = new Date(taskEditForm.start_date).getTime();
      const due_date_ts = new Date(taskEditForm.due_date).getTime();

      // Determine API type based on workloadData
      const apiType = workloadData?.teamId ? 'clickup' : 
                      workloadData?.orbitId ? 'orbit' : 'clickup'; // default to clickup

      const updatePayload = {
        start_date: start_date_ts,
        due_date: due_date_ts
      };

      // Add teamId for ClickUp or orbitId for Orbit
      if (apiType === 'clickup') {
        updatePayload.teamId = workloadData.teamId;
        await workloadAPI.updateTask(taskEditForm.taskId, updatePayload);
      } else {
        updatePayload.orbitId = workloadData.orbitId;
        await workloadAPI.updateTaskOrbit(taskEditForm.taskId, updatePayload);
      }

      toast.success('Task updated successfully!');
      
      // Update local workloadData optimistically (no page reload)
      if (workloadData && workloadData.tasks) {
        setWorkloadData({
          ...workloadData,
          tasks: workloadData.tasks.map(t => 
            t.id === taskEditForm.taskId 
              ? { ...t, start_date: start_date_ts.toString(), due_date: due_date_ts.toString() }
              : t
          )
        });
      }
      
      setEditingTask(null);
      setTaskEditForm({ taskId: '', start_date: '', due_date: '' });
      setShowEditTaskModal(false);
    } catch (err) {
      console.error('Error updating task:', err);
      toast.error(err.response?.data?.error || 'Failed to update task dates');
    } finally {
      setSavingTask(false);
    }
  };

  const calculateEligibleDates = useCallback((data, checkoutRequests = [], unclosedDate = null) => {
    const eligible = calculateCheckoutEligibleDates({
      attendanceData: data,
      checkoutRequests,
      unclosedDate
    });

    console.log('📅 Eligible dates calculated:', eligible.length, 'dates');
    console.log('📅 Eligible dates objects:', eligible.map(d => moment(d).format('YYYY-MM-DD')));

    setEligibleDates(eligible);

    if (eligible.length === 0) {
      if (requestDate) {
        setRequestDate(null);
      }
      return;
    }

    const currentDateStillEligible = requestDate && eligible.some(date => 
      moment(date).isSame(requestDate, 'day')
    );

    if (!currentDateStillEligible) {
      setRequestDate(eligible[0]);
    }
  }, [requestDate]);

  const checkoutEligibilityData = useMemo(
    () => getCheckoutEligibilitySource({ attendanceData, calendarData }),
    [attendanceData, calendarData]
  );

  const loadCheckInStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/mobile-attendance/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('📋 Check-in status response:', response.data);
      
      if (response.data?.hasUnclosedCheckIn && response.data?.unclosedCheckInDate) {
        const unclosedDate = response.data.unclosedCheckInDate;
        console.log('✅ Found unclosed check-in date:', unclosedDate);
        setUnclosedCheckInDate(unclosedDate);
        return unclosedDate;
      } else {
        console.log('ℹ️ No unclosed check-in found');
        setUnclosedCheckInDate(null);
        return null;
      }
    } catch (err) {
      console.error('Failed to load check-in status:', err);
      setUnclosedCheckInDate(null);
      return null;
    }
  };

  const loadMyRequests = async (attendanceContext = attendanceData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/checkout-requests/my-requests', {
        params: { limit: 30 },
        headers: { Authorization: `Bearer ${token}` }
      });
      const requests = response.data.requests || [];
      setMyRequests(requests);

      // Load check-in status to get unclosed check-in date
      const unclosedDate = await loadCheckInStatus();

      const context = checkoutEligibilityData || attendanceContext || attendanceData;
      calculateEligibleDates(context, requests, unclosedDate);
      
      return requests;
    } catch (err) {
      console.error('Failed to load checkout requests:', err);
      const unclosedDate = await loadCheckInStatus();
      const context = checkoutEligibilityData || attendanceContext || attendanceData;
      calculateEligibleDates(context, [], unclosedDate);
      return [];
    }
  };

  const loadMyLeaveRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/leave-requests/my-requests', {
        params: { limit: 5 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyLeaveRequests(response.data.requests || []);
    } catch (err) {
      console.error('Failed to load leave requests:', err);
    }
  };

  const loadMyExtraHoursRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/extra-hours-requests/my-requests', {
        params: { limit: 5 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyExtraHoursRequests(response.data.requests || []);
    } catch (err) {
      console.error('Failed to load extra hours requests:', err);
    }
  };

  const loadCalendarData = async (monthValue = calendarMonth) => {
    try {
      setCalendarLoading(true);
      const token = localStorage.getItem('token');
      const startDate = moment(monthValue).startOf('month').format('YYYY-MM-DD');
      const endDate = moment(monthValue).endOf('month').format('YYYY-MM-DD');

      const response = await axios.get('/api/user/summary', {
        params: { startDate, endDate, fullDailyHours: true },
        headers: { Authorization: `Bearer ${token}` }
      });

      setCalendarData(response.data);
    } catch (err) {
      console.error('Failed to load calendar data:', err);
    } finally {
      setCalendarLoading(false);
    }
  };

  const loadCorrectionRequests = async (monthValue = calendarMonth) => {
    try {
      const startDate = moment(monthValue).startOf('month').format('YYYY-MM-DD');
      const endDate = moment(monthValue).endOf('month').format('YYYY-MM-DD');
      const response = await attendanceCorrectionsAPI.getMyRequests({ startDate, endDate });
      setMyCorrectionRequests(response.data.requests || []);
    } catch (err) {
      console.error('Failed to load correction requests:', err);
    }
  };

  const getMonthKey = (dateValue) => moment(dateValue).format('YYYY-MM');

  const loadLimitsForMonth = async (monthKey, options = {}) => {
    if (!monthKey) return null;
    if (!options.force && limitsByMonth[monthKey]) {
      return limitsByMonth[monthKey];
    }

    try {
      setLimitsLoading(true);
      const response = await leaveRequestsAPI.getLimits({ month: monthKey });
      setLimitsByMonth(prev => ({
        ...prev,
        [monthKey]: response.data
      }));
      return response.data;
    } catch (err) {
      console.error('Failed to load leave limits:', err);
      return null;
    } finally {
      setLimitsLoading(false);
    }
  };

  // Cancel handlers
  const handleCancelLeaveRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this leave request? Your manager will be notified.')) {
      return;
    }

    try {
      await leaveRequestsAPI.cancel(requestId);
      toast.success('Leave request cancelled successfully');
      await loadMyLeaveRequests();
      await loadLimitsForMonth(moment().format('YYYY-MM'), { force: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel leave request');
    }
  };

  const handleCancelCheckoutRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this checkout request? Your manager will be notified.')) {
      return;
    }

    try {
      await checkoutRequestsAPI.cancel(requestId);
      toast.success('Checkout request cancelled successfully');
      await loadMyRequests();
      refreshCheckInWidget();
      await loadLimitsForMonth(moment().format('YYYY-MM'), { force: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel checkout request');
    }
  };

  const handleCancelExtraHoursRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this extra hours request? Your manager will be notified.')) {
      return;
    }

    try {
      await extraHoursRequestsAPI.cancel(requestId);
      toast.success('Extra hours request cancelled successfully');
      await loadMyExtraHoursRequests();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel extra hours request');
    }
  };

  // Load holidays for working days calculation
  const loadHolidays = async () => {
    try {
      const currentYear = moment().year();
      const nextYear = currentYear + 1;
      const response = await policiesAPI.getHolidays({ year: currentYear });
      const nextYearResponse = await policiesAPI.getHolidays({ year: nextYear });
      const allHolidays = [...(response.data || []), ...(nextYearResponse.data || [])];
      setHolidays(allHolidays);
    } catch (err) {
      console.error('Failed to load holidays:', err);
      setHolidays([]);
    }
  };

  // Load upcoming holidays for display
  const loadUpcomingHolidays = async (employmentTypeParam = null) => {
    try {
      const today = moment().format('YYYY-MM-DD');
      const employmentType = employmentTypeParam || user?.employementType || attendanceData?.user?.employmentType || 'Full-Time';
      
      // Fetch holidays from today onwards
      const currentYear = moment().year();
      const nextYear = currentYear + 1;
      const response = await policiesAPI.getHolidays({ year: currentYear });
      const nextYearResponse = await policiesAPI.getHolidays({ year: nextYear });
      const allHolidays = [...(response.data || []), ...(nextYearResponse.data || [])];
      
      // Filter upcoming holidays
      const upcoming = allHolidays
        .filter(h => {
          const holidayDate = moment(h.holidayDate).format('YYYY-MM-DD');
          // Only include future holidays or today
          if (holidayDate < today) return false;
          // Filter by employment type
          return h.applicableTo === 'Both' || h.applicableTo === employmentType;
        })
        .sort((a, b) => moment(a.holidayDate).diff(moment(b.holidayDate)))
        .slice(0, 12); // Limit to next 12 holidays
      
      setUpcomingHolidays(upcoming);
    } catch (err) {
      console.error('Failed to load upcoming holidays:', err);
      setUpcomingHolidays([]);
    }
  };

  // Calculate working days excluding weekends and holidays
  const calculateWorkingDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    
    const start = moment(startDate);
    const end = moment(endDate);
    
    // Create a Set of holiday dates for quick lookup
    const holidayDates = new Set(
      holidays.map(h => moment(h.holidayDate).format('YYYY-MM-DD'))
    );
    
    let workingDays = 0;
    for (let date = start.clone(); date.isSameOrBefore(end); date.add(1, 'day')) {
      const dayOfWeek = date.day();
      const dateStr = date.format('YYYY-MM-DD');
      
      // Exclude weekends (Saturday = 6, Sunday = 0) and holidays
      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !holidayDates.has(dateStr)) {
        workingDays++;
      }
    }
    
    return workingDays;
  };

  // Calculate working days excluding holidays with employment type filtering and policy workDays
  const calculateWorkingDaysExcludingHolidays = (startDate, endDate, employmentType, workDays = null) => {
    if (!startDate || !endDate) return { workingDays: 0, holidayCount: 0 };
    
    const start = moment(startDate);
    const end = moment(endDate);
    
    // Filter holidays by employment type
    const applicableHolidays = holidays.filter(h => {
      return h.applicableTo === 'Both' || 
             h.applicableTo === employmentType ||
             (employmentType === 'Full-Time' && h.applicableTo === 'Full-Time') ||
             (employmentType === 'Intern' && h.applicableTo === 'Intern');
    });
    
    // Create a Set of applicable holiday dates for quick lookup
    const holidayDates = new Set(
      applicableHolidays.map(h => moment(h.holidayDate).format('YYYY-MM-DD'))
    );
    
    // Map day names to moment day numbers
    const dayNameToNumber = {
      'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
      'thursday': 4, 'friday': 5, 'saturday': 6
    };
    
    let workingDaysCount = 0;
    let holidayCount = 0;
    
    for (let date = start.clone(); date.isSameOrBefore(end); date.add(1, 'day')) {
      const dateStr = date.format('YYYY-MM-DD');
      const dayOfWeek = date.day(); // 0=Sunday, 1=Monday, etc.
      
      // Check if it's a holiday
      if (holidayDates.has(dateStr)) {
        holidayCount++;
        continue; // Skip holidays
      }
      
      // Check if it's a working day based on policy
      if (workDays && workDays.length > 0) {
        // Normalize workDays to lowercase for comparison
        const normalizedWorkDays = workDays.map(day => day.toLowerCase());
        const dayName = Object.keys(dayNameToNumber).find(key => dayNameToNumber[key] === dayOfWeek);
        
        if (normalizedWorkDays.includes(dayName)) {
          workingDaysCount++;
        }
      } else {
        // No workDays policy: exclude weekends only (Saturday = 6, Sunday = 0)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          workingDaysCount++;
        }
      }
    }
    
    return {
      workingDays: workingDaysCount,
      holidayCount: holidayCount,
      actualWorkingDays: workingDaysCount // workingDays already excludes holidays
    };
  };

  const currentMonthKey = moment().format('YYYY-MM');
  const leaveMonthKey = leaveStartDate ? getMonthKey(leaveStartDate) : currentMonthKey;
  const leaveMonthSummary = limitsByMonth[leaveMonthKey];
  const leaveAnnualSummary = leaveMonthSummary?.annual;
  const leaveMonthlySummary = leaveMonthSummary?.monthly;
  const leaveLimitInfo = leaveAnnualSummary?.leaveLimits?.[leaveType];
  const leaveMonthlyUsed = leaveMonthlySummary?.leaveLimits?.[leaveType]?.used ?? 0;
  const leaveRequestedDays = leaveType === 'Short Leave'
    ? 0
    : leaveType === 'Half Day'
      ? 0.5
      : (calculateWorkingDays(leaveStartDate, leaveEndDate) || 0);
  const leaveProjectedUsed = (leaveLimitInfo?.used || 0) + leaveRequestedDays;
  const willExceedLeaveLimit = Boolean(
    leaveLimitInfo &&
    !leaveLimitInfo.isExempt &&
    leaveLimitInfo.limit !== null &&
    leaveProjectedUsed > leaveLimitInfo.limit
  );
  const checkoutMonthKey = requestDate ? getMonthKey(requestDate) : currentMonthKey;
  const checkoutMonthSummary = limitsByMonth[checkoutMonthKey];
  const checkoutLimitInfo = checkoutMonthSummary?.monthly?.checkoutLimit;
  const checkoutProjectedUsed = (checkoutLimitInfo?.used || 0) + (requestDate ? 1 : 0);
  const willExceedCheckoutLimit = Boolean(
    checkoutLimitInfo &&
    checkoutLimitInfo.limit !== null &&
    requestDate &&
    checkoutProjectedUsed > checkoutLimitInfo.limit
  );
  const formatLimitValue = (value) => {
    if (!Number.isFinite(value)) return value;
    return Number.isInteger(value) ? value : value.toFixed(1);
  };

  const calendarDailyMap = useMemo(() => {
    if (!calendarData?.dailyHours) return {};
    return calendarData.dailyHours.reduce((acc, day) => {
      const key = moment(day.date).format('YYYY-MM-DD');
      acc[key] = day;
      return acc;
    }, {});
  }, [calendarData]);

  const calendarHolidayMap = useMemo(() => {
    if (!calendarData?.holidays) return {};
    return calendarData.holidays.reduce((acc, holiday) => {
      const key = moment(holiday.holidayDate).format('YYYY-MM-DD');
      acc[key] = holiday;
      return acc;
    }, {});
  }, [calendarData]);

  const calendarLeaveMap = useMemo(() => {
    if (!calendarData?.leaveCalendar) return {};
    return calendarData.leaveCalendar.reduce((acc, entry) => {
      acc[entry.date] = entry.leaves || [];
      return acc;
    }, {});
  }, [calendarData]);

  const correctionRequestMap = useMemo(() => {
    if (!myCorrectionRequests?.length) return {};
    return myCorrectionRequests.reduce((acc, request) => {
      acc[request.requestDate] = request;
      return acc;
    }, {});
  }, [myCorrectionRequests]);

  const checkoutRequestMap = useMemo(() => {
    if (!myRequests?.length) return {};
    const statusPriority = {
      approved: 4,
      pending: 3,
      rejected: 2,
      cancelled: 1
    };

    return myRequests.reduce((acc, request) => {
      const dateKey = request.requestDate;
      const existing = acc[dateKey];

      if (!existing) {
        acc[dateKey] = request;
        return acc;
      }

      const existingPriority = statusPriority[(existing.status || '').toLowerCase()] || 0;
      const nextPriority = statusPriority[(request.status || '').toLowerCase()] || 0;

      if (nextPriority > existingPriority) {
        acc[dateKey] = request;
        return acc;
      }

      if (nextPriority === existingPriority) {
        const existingTs = new Date(existing.reviewedAt || existing.updatedAt || existing.createdAt || 0).getTime();
        const nextTs = new Date(request.reviewedAt || request.updatedAt || request.createdAt || 0).getTime();
        if (nextTs > existingTs) {
          acc[dateKey] = request;
        }
      }

      return acc;
    }, {});
  }, [myRequests]);

  const calendarWorkDays = calendarData?.workingPolicy?.workDays || null;

  const calendarWeeks = useMemo(() => {
    const startOfMonth = moment(calendarMonth).startOf('month');
    const endOfMonth = moment(calendarMonth).endOf('month');
    const start = startOfMonth.clone().startOf('isoWeek');
    const end = endOfMonth.clone().endOf('isoWeek');

    const weeks = [];
    let current = start.clone();
    while (current.isSameOrBefore(end, 'day')) {
      const week = [];
      for (let i = 0; i < 7; i += 1) {
        week.push(current.clone());
        current.add(1, 'day');
      }
      weeks.push(week);
    }

    return weeks;
  }, [calendarMonth]);

  const calendarDays = useMemo(() => {
    const startOfMonth = moment(calendarMonth).startOf('month');
    const endOfMonth = moment(calendarMonth).endOf('month');
    const days = [];
    let current = startOfMonth.clone();
    while (current.isSameOrBefore(endOfMonth, 'day')) {
      days.push(current.clone());
      current.add(1, 'day');
    }
    return days;
  }, [calendarMonth]);

  const isDateEligible = (date) => {
    if (!date || !eligibleDates || eligibleDates.length === 0) {
      console.log('❌ isDateEligible: No date or no eligible dates', { date, eligibleDatesLength: eligibleDates?.length });
      return false;
    }
    
    const dateKey = moment(date).format('YYYY-MM-DD');
    const isEligible = eligibleDates.some(eligible => {
      if (!eligible) return false;
      const eligibleKey = moment(eligible).format('YYYY-MM-DD');
      return eligibleKey === dateKey;
    });
    
    console.log('🔍 isDateEligible check:', { dateKey, isEligible, eligibleDatesKeys: eligibleDates.map(d => moment(d).format('YYYY-MM-DD')) });
    return isEligible;
  };

  const getCalendarDayState = useCallback((dateValue) => {
    const date = moment(dateValue);
    const dateKey = date.format('YYYY-MM-DD');
    const isCurrentMonth = date.isSame(calendarMonth, 'month');
    const isFuture = date.isAfter(moment(), 'day');
    const dayOfWeek = date.day();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isWorkingDay = calendarWorkDays
      ? calendarWorkDays.map(day => day.toLowerCase()).includes(
          ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek]
        )
      : !isWeekend;

    const attendance = calendarDailyMap[dateKey];
    const holiday = calendarHolidayMap[dateKey];
    const leaveEntries = calendarLeaveMap[dateKey] || [];
    const correctionRequest = correctionRequestMap[dateKey];
    const checkoutRequest = checkoutRequestMap[dateKey];
    const checkoutRequestStatus = (checkoutRequest?.status || '').toLowerCase();
    const hasRawActiveCheckoutRequest = checkoutRequestStatus === 'pending' || checkoutRequestStatus === 'approved';
    const hasApprovedCheckoutRequest = checkoutRequestStatus === 'approved';
    const beforeTodayCheckoutCutoff = isBeforeTodayCheckoutRequestCutoff(date);
    const totalHours = attendance ? (Number(attendance.hoursWorked || 0) + Number(attendance.extraHours || 0)) : 0;
    const hasAttendance = Boolean(attendance);
    const hasCheckIn = Boolean(attendance?.firstCheckIn);
    const hasCheckOut = Boolean(attendance?.lastCheckOut);
    const hasResolvedApprovedCheckout = hasApprovedCheckoutRequest && hasAttendance && (hasCheckIn || totalHours > 0);
    const hasCompletedAttendance = hasAttendance && (totalHours > 0 || hasCheckOut);
    const hasActiveCheckoutRequest = hasRawActiveCheckoutRequest && !hasCompletedAttendance && !beforeTodayCheckoutCutoff;
    const hasCalendarData = Boolean(calendarData);
    const isIncompleteWorkingDay =
      hasCalendarData &&
      isCurrentMonth &&
      isWorkingDay &&
      !isFuture &&
      !holiday &&
      leaveEntries.length === 0;
    const hasMissingCheckout = isIncompleteWorkingDay && !beforeTodayCheckoutCutoff && hasAttendance && hasCheckIn && !hasCheckOut && !hasResolvedApprovedCheckout;
    const hasMissingAttendance =
      isIncompleteWorkingDay &&
      !beforeTodayCheckoutCutoff &&
      !hasMissingCheckout &&
      !hasResolvedApprovedCheckout &&
      (!hasAttendance || (totalHours === 0 && !hasCheckOut));

    return {
      dateKey,
      attendance,
      holiday,
      leaveEntries,
      correctionRequest,
      checkoutRequest,
      hasActiveCheckoutRequest,
      hasApprovedCheckoutRequest,
      totalHours,
      hasAttendance,
      hasCheckIn,
      hasCheckOut,
      hasMissingCheckout,
      hasMissingAttendance,
    };
  }, [
    calendarData,
    calendarDailyMap,
    calendarHolidayMap,
    calendarLeaveMap,
    correctionRequestMap,
    checkoutRequestMap,
    calendarMonth,
    calendarWorkDays
  ]);

  useEffect(() => {
    if (leaveStartDate) {
      loadLimitsForMonth(getMonthKey(leaveStartDate));
    }
  }, [leaveStartDate]);

  useEffect(() => {
    if (requestDate) {
      loadLimitsForMonth(getMonthKey(requestDate));
    }
  }, [requestDate]);

  useEffect(() => {
    loadCalendarData(calendarMonth);
    loadCorrectionRequests(calendarMonth);
  }, [calendarMonth]);

  useEffect(() => {
    if (showRequestModal) {
      // Reload check-in status and requests when modal opens to get latest data
      const refreshData = async () => {
        console.log('🔄 Modal opened, refreshing data...');
        // loadMyRequests will call loadCheckInStatus and calculateEligibleDates
        await loadMyRequests(attendanceData);
      };
      refreshData();
    }
  }, [showRequestModal]);

  const handleSubmitRequest = async () => {
    try {
      if (!requestDate) {
        toast.error('Please select a date');
        return;
      }

      if (!isDateEligible(requestDate)) {
        toast.error('Selected date already has complete attendance. Please select a date with missing checkout.');
        return;
      }

      if (requestReason.length > 500) {
        toast.error('Reason cannot exceed 500 characters');
        return;
      }

      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      // Combine date and time
      const checkoutDateTime = moment(requestDate)
        .set({
          hour: parseInt(requestTime.split(':')[0]),
          minute: parseInt(requestTime.split(':')[1])
        })
        .toISOString();

      // Execute reCAPTCHA
      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha('checkout_request');
      } catch (recaptchaError) {
        console.error('reCAPTCHA error:', recaptchaError);
        toast.error('reCAPTCHA verification failed. Please refresh the page and try again.');
        setSubmitting(false);
        return;
      }

      await axios.post(
        '/api/checkout-requests/submit',
        {
          requestDate: moment(requestDate).format('YYYY-MM-DD'),
          requestedCheckoutTime: checkoutDateTime,
          reason: requestReason,
          recaptchaToken
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Checkout request submitted successfully! Your manager will be notified.');
      setShowRequestModal(false);
      setRequestReason('');
      setRequestTime('18:00');
      setRequestDate(null);
      await loadMyRequests();
      refreshCheckInWidget();
      await loadLimitsForMonth(moment(requestDate).format('YYYY-MM'), { force: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit request');
      if (err.response?.data?.overLimitNotice) {
        toast.info(err.response.data.overLimitNotice);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitLeave = async () => {
    try {
      if (!leaveReason.trim()) {
        toast.warning('Please provide a reason for your leave');
        return;
      }

      if (leaveReason.length > 500) {
        toast.error('Reason cannot exceed 500 characters');
        return;
      }

      setSubmittingLeave(true);
      const token = localStorage.getItem('token');

      await axios.post(
        '/api/leave-requests/submit',
        {
          startDate: moment(leaveStartDate).format('YYYY-MM-DD'),
          endDate: moment(leaveEndDate).format('YYYY-MM-DD'),
          leaveType,
          reason: leaveReason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Leave request submitted successfully! Your manager will be notified.');
      setShowLeaveModal(false);
      setLeaveReason('');
      setLeaveType('Casual Leave');
      setLeaveStartDate(new Date());
      setLeaveEndDate(new Date());
      loadMyLeaveRequests();
      await loadLimitsForMonth(moment(leaveStartDate).format('YYYY-MM'), { force: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit leave request');
      if (err.response?.data?.overLimitNotice) {
        toast.info(err.response.data.overLimitNotice);
      }
    } finally {
      setSubmittingLeave(false);
    }
  };

  const openCheckoutRequestModal = useCallback((dateValue = null) => {
    let targetDate = dateValue ? moment(dateValue).toDate() : null;
    if (!targetDate) {
      if (unclosedCheckInDate) {
        targetDate = moment(unclosedCheckInDate).toDate();
      } else if (eligibleDates.length > 0) {
        targetDate = eligibleDates[0];
      }
    }
    setRequestDate(targetDate);
    setRequestReason('');
    setRequestTime('18:00');
    setShowRequestModal(true);
  }, [eligibleDates, unclosedCheckInDate]);

  const handleOpenCorrectionModal = (dateValue) => {
    const dayState = getCalendarDayState(dateValue);
    if (dayState.hasMissingCheckout) {
      setCheckoutRedirectWarning({
        date: moment(dateValue).toDate(),
        firstCheckIn: dayState.attendance?.firstCheckIn || null
      });
      return;
    }

    setCorrectionDate(dateValue);
    setCorrectionReason('');
    setCorrectionCheckInTime('09:00');
    setCorrectionCheckOutTime('18:00');
    setShowCorrectionModal(true);
  };

  const handleContinueToCheckoutRequest = () => {
    const warningDate = checkoutRedirectWarning?.date || null;
    setCheckoutRedirectWarning(null);
    openCheckoutRequestModal(warningDate);
  };

  const handleSubmitCorrectionRequest = async () => {
    try {
      if (!correctionDate) {
        toast.error('Please select a date');
        return;
      }

      if (!correctionReason.trim()) {
        toast.warning('Please provide a reason for the correction');
        return;
      }

      const dayState = getCalendarDayState(correctionDate);
      if (dayState.hasMissingCheckout) {
        setShowCorrectionModal(false);
        setCheckoutRedirectWarning({
          date: moment(correctionDate).toDate(),
          firstCheckIn: dayState.attendance?.firstCheckIn || null
        });
        return;
      }

      const requestedCheckIn = correctionCheckInTime
        ? moment(correctionDate).hour(parseInt(correctionCheckInTime.split(':')[0])).minute(parseInt(correctionCheckInTime.split(':')[1])).toISOString()
        : null;
      const requestedCheckOut = correctionCheckOutTime
        ? moment(correctionDate).hour(parseInt(correctionCheckOutTime.split(':')[0])).minute(parseInt(correctionCheckOutTime.split(':')[1])).toISOString()
        : null;

      if (requestedCheckIn && requestedCheckOut && moment(requestedCheckOut).isSameOrBefore(moment(requestedCheckIn))) {
        toast.error('Check-out time must be after check-in time');
        return;
      }

      setSubmittingCorrection(true);
      await attendanceCorrectionsAPI.submit({
        requestDate: moment(correctionDate).format('YYYY-MM-DD'),
        reason: correctionReason.trim(),
        requestedCheckInTime: requestedCheckIn,
        requestedCheckOutTime: requestedCheckOut
      });

      toast.success('Attendance correction request submitted!');
      setShowCorrectionModal(false);
      setCorrectionReason('');
      await loadCorrectionRequests(calendarMonth);
    } catch (err) {
      if (err.response?.data?.requestType === 'checkout') {
        setShowCorrectionModal(false);
        setCheckoutRedirectWarning({
          date: moment(correctionDate).toDate(),
          firstCheckIn: err.response?.data?.checkInTime || null
        });
        return;
      }
      toast.error(err.response?.data?.error || 'Failed to submit correction request');
    } finally {
      setSubmittingCorrection(false);
    }
  };

  const handleSubmitExtraHours = async () => {
    try {
      if (!extraHoursReason.trim()) {
        toast.warning('Please provide a reason for your extra hours request');
        return;
      }

      if (extraHoursRequested < 0.5) {
        toast.error('Extra hours must be at least 0.5 hours');
        return;
      }

      // Validate start and end time if both provided
      if (extraHoursStartTime && extraHoursEndTime) {
        const start = moment(extraHoursStartTime);
        const end = moment(extraHoursEndTime);
        if (end.isSameOrBefore(start)) {
          toast.error('End time must be after start time');
          return;
        }
      }

      setSubmittingExtraHours(true);
      const token = localStorage.getItem('token');

      await axios.post(
        '/api/extra-hours-requests/submit',
        {
          requestDate: moment(extraHoursDate).format('YYYY-MM-DD'),
          requestedHours: parseFloat(extraHoursRequested),
          reason: extraHoursReason.trim(),
          startTime: extraHoursStartTime ? moment(extraHoursStartTime).toISOString() : null,
          endTime: extraHoursEndTime ? moment(extraHoursEndTime).toISOString() : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Extra hours request submitted successfully! Your manager will be notified.');
      setShowExtraHoursModal(false);
      setExtraHoursReason('');
      setExtraHoursRequested(1);
      setExtraHoursStartTime(null);
      setExtraHoursEndTime(null);
      setExtraHoursDate(new Date());
      loadMyExtraHoursRequests();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit extra hours request');
    } finally {
      setSubmittingExtraHours(false);
    }
  };

  const handleLogout = () => {
    // Use AuthContext logout which clears both cookies and localStorage
    logout();
  };

  const getCompletionStatus = (hoursWorked, requiredHours) => {
    if (!requiredHours) return { status: 'unknown', percentage: 0 };
    
    const percentage = (hoursWorked / requiredHours) * 100;
    
    if (percentage >= 100) {
      return { status: 'complete', percentage: Math.min(percentage, 100) };
    } else if (percentage >= 80) {
      return { status: 'warning', percentage };
    } else {
      return { status: 'incomplete', percentage };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'complete':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'incomplete':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <CheckCircleIcon className="h-5 w-5" />;
      case 'warning':
      case 'incomplete':
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      default:
        return <ClockIcon className="h-5 w-5" />;
    }
  };

  useEffect(() => {
    if (!checkoutEligibilityData) {
      setEligibleDates([]);
      return;
    }

    calculateEligibleDates(checkoutEligibilityData, myRequests, unclosedCheckInDate);
  }, [checkoutEligibilityData, myRequests, unclosedCheckInDate, calculateEligibleDates]);

  const formatPeriodLabel = () => {
    switch (selectedPeriod) {
      case 'week':
        return 'This Week';
      case 'month':
        return 'This Month';
      case 'year':
        return 'This Year';
      case 'custom':
        return `${moment(customStartDate).format('MMM DD')} - ${moment(customEndDate).format('MMM DD, YYYY')}`;
      default:
        return 'This Month';
    }
  };

  const getPeriodDateRange = () => {
    switch (selectedPeriod) {
      case 'week':
        return `${moment().startOf('isoWeek').format('MMM DD')} - ${moment().endOf('isoWeek').format('MMM DD, YYYY')}`;
      case 'month':
        return `${moment().startOf('month').format('MMM DD')} - ${moment().endOf('month').format('MMM DD, YYYY')}`;
      case 'year':
        return `${moment().startOf('year').format('MMM DD, YYYY')} - ${moment().endOf('year').format('MMM DD, YYYY')}`;
      case 'custom':
        return `${moment(customStartDate).format('MMM DD')} - ${moment(customEndDate).format('MMM DD, YYYY')}`;
      default:
        return `${moment().startOf('month').format('MMM DD')} - ${moment().endOf('month').format('MMM DD, YYYY')}`;
    }
  };

  const getPeriodLabel = () => {
    switch (selectedPeriod) {
      case 'week':
        return 'week';
      case 'month':
        return 'month';
      case 'year':
        return 'year';
      case 'custom':
        return 'period';
      default:
        return 'month';
    }
  };

  const headerActions = (
    <div className="flex w-full flex-wrap items-center gap-2">
      <button
        onClick={() => setShowProfileEdit(true)}
        className="inline-flex h-10 flex-1 min-w-[94px] items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 px-3 text-xs font-bold text-white shadow-sm backdrop-blur hover:bg-white/20"
        title="Edit Profile"
        type="button"
      >
        <PencilIcon className="h-4 w-4" />
        <span>Profile</span>
      </button>
      <button
        onClick={() => navigate('/my-kpi')}
        className="inline-flex h-10 flex-1 min-w-[84px] items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 px-3 text-xs font-bold text-white shadow-sm backdrop-blur hover:bg-white/20"
        title="My KPI"
        type="button"
      >
        <ChartBarIcon className="h-4 w-4" />
        <span>KPI</span>
      </button>
      <button
        onClick={() => navigate('/my-feedback')}
        className={`relative inline-flex h-10 flex-1 min-w-[104px] items-center justify-center gap-2 rounded-xl border px-3 text-xs font-bold shadow-sm backdrop-blur ${
          unreadRepliesCount > 0
            ? 'border-emerald-300/50 bg-emerald-400/20 text-white'
            : 'border-white/15 bg-white/10 text-white hover:bg-white/20'
        }`}
        title={unreadRepliesCount > 0 ? `${unreadRepliesCount} unread repl${unreadRepliesCount > 1 ? 'ies' : 'y'}` : 'My Feedback'}
        type="button"
      >
        <ChatBubbleLeftRightIcon className="h-4 w-4" />
        <span>Feedback</span>
        {unreadRepliesCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadRepliesCount > 99 ? '99+' : unreadRepliesCount}
          </span>
        )}
      </button>
      <DashboardSwitcher />
      <NotificationBell />
      <button
        onClick={handleLogout}
        className="inline-flex h-10 flex-1 min-w-[94px] items-center justify-center gap-2 rounded-xl border border-red-300/30 bg-red-400/15 px-3 text-xs font-bold text-white shadow-sm backdrop-blur hover:bg-red-400/25"
        title="Logout"
        type="button"
      >
        <ArrowRightOnRectangleIcon className="h-4 w-4" />
        <span>Logout</span>
      </button>
    </div>
  );

  const todayCard = (
    <div className="space-y-3">
      {deviceLock?.attendanceBlocked && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-800 shadow-sm">
          This phone is locked for attendance use by <strong>{deviceLock.lockedEmail}</strong>.
          {deviceLock.pendingRequest ? ' Your device unlock request is pending review.' : ' Request an unlock to use attendance with this account.'}
        </div>
      )}
      {deviceRegistration?.registered === false && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900 shadow-sm">
          <p>{deviceRegistration.message || 'This phone is not your registered attendance device.'}</p>
          {deviceChangePending ? (
            <p className="mt-2 font-medium">Your device change request is pending review.</p>
          ) : (
            <button
              type="button"
              onClick={handleRequestDeviceChange}
              className="mt-2 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
            >
              Request device change
            </button>
          )}
        </div>
      )}
      <CheckInWidget
        refreshSignal={checkInWidgetRefreshToken}
        deviceLock={deviceLock}
        onRequestUnlock={handleRequestDeviceUnlock}
      />
      {attendanceData?.workingPolicy && (
        <div className="space-y-3 rounded-[28px] bg-white p-4 shadow-sm ring-1 ring-slate-200">
          {(() => {
            const dailyHours = Number(attendanceData.workingPolicy.requiredDailyHours || 0);
            const leaveDays = Number(attendanceData.summary?.leaveDays || 0);
            const leaveHoursDeducted = Number(attendanceData.summary?.leaveHoursDeducted || 0);
            const policyWorkingDays = Number(attendanceData.summary?.actualWorkingDays || attendanceData.summary?.totalDays || 0);
            const workingDays = Math.max(0, policyWorkingDays - leaveDays);
            // Subtract the exact exempted hours so a Half Day removes half a day, not a whole one
            const requiredHours = Math.max(0, dailyHours * policyWorkingDays - leaveHoursDeducted);
            const totalHoursWorked = Number(attendanceData.summary?.totalHours || 0);
            const hoursNeeded = Math.max(0, requiredHours - totalHoursWorked);
            const completionPercent = requiredHours > 0 ? Math.min(100, Math.round((totalHoursWorked / requiredHours) * 100)) : 0;

            return (
              <>
                <div className="rounded-2xl bg-gradient-to-r from-blue-500 to-violet-600 p-5 text-white shadow-lg shadow-blue-500/20">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/20">
                      <ChartBarIcon className="h-7 w-7" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-white/90">Working Policy</div>
                      <div className="truncate text-xl font-black">{attendanceData.workingPolicy.policyName || 'Assigned policy'}</div>
                      <div className="mt-1 text-sm font-semibold text-white/85">
                        {dailyHours} hours/day - {formatDayCount(workingDays)} working days
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/20">
                    <div className="h-full rounded-full bg-white" style={{ width: `${completionPercent}%` }} />
                  </div>
                </div>

                <div className={`rounded-2xl border p-4 text-sm shadow-sm ${
                  hoursNeeded > 0 ? 'border-yellow-200 bg-yellow-50 text-slate-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                      hoursNeeded > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      <ClockIcon className="h-5 w-5" />
                    </div>
                    <div>
                      {hoursNeeded > 0
                        ? `You need ${hoursNeeded.toFixed(1)} more hours to reach your target.`
                        : 'You have reached your working hours target.'}
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}
      {eligibleDates.length > 0 && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-gray-700">
          You have {eligibleDates.length} date{eligibleDates.length > 1 ? 's' : ''} that need checkout time.
        </div>
      )}
    </div>
  );

  const selectedDashboardStat = dashboardStats.find((tile) => tile.id === activeDashboardSection);

  const openDashboardDetail = (sectionId) => {
    if (sectionId === DASHBOARD_SECTIONS.holidays) {
      setShowAllHolidayDetails(false);
    }
    setActiveDashboardSection(sectionId);
  };

  const closeDashboardDetail = () => {
    setActiveDashboardSection(null);
  };

  const getCompactCalendarStatus = (dayState) => {
    if (dayState.holiday) {
      return { label: 'Holiday', dotClass: 'bg-amber-500', textClass: 'text-amber-700' };
    }
    if (dayState.leaveEntries?.length > 0) {
      return { label: 'Leave', dotClass: 'bg-purple-500', textClass: 'text-purple-700' };
    }
    if (dayState.hasActiveCheckoutRequest) {
      return { label: 'Checkout pending', dotClass: 'bg-orange-400', textClass: 'text-orange-700' };
    }
    if (dayState.hasAttendance && (dayState.totalHours > 0 || dayState.hasCheckOut)) {
      return { label: 'Present', dotClass: 'bg-green-500', textClass: 'text-green-700' };
    }
    if (dayState.hasMissingCheckout) {
      return { label: 'Checkout missing', dotClass: 'bg-orange-500', textClass: 'text-orange-700' };
    }
    if (dayState.hasMissingAttendance) {
      return { label: 'Missing', dotClass: 'bg-red-500', textClass: 'text-red-700' };
    }
    return { label: '', dotClass: 'bg-slate-200', textClass: 'text-slate-400' };
  };

  const dashboardRequestItems = [
    ...myLeaveRequests.map((request) => ({
      id: `leave-${request.id}`,
      title: request.leaveType || 'Leave request',
      meta: `${moment(request.startDate).format('MMM D')} - ${moment(request.endDate).format('MMM D')}`,
      status: request.status,
      note: request.reason,
    })),
    ...myRequests.map((request) => ({
      id: `checkout-${request.id}`,
      title: 'Checkout request',
      meta: `${moment(request.requestDate).format('MMM D')} @ ${moment(request.requestedCheckoutTime).format('h:mm A')}`,
      status: request.status,
      note: request.reason,
    })),
    ...myExtraHoursRequests.map((request) => ({
      id: `extra-${request.id}`,
      title: 'Extra hours',
      meta: moment(request.requestDate || request.date).format('MMM D'),
      status: request.status,
      note: request.reason,
    })),
  ];

  const dashboardTaskItems = myTasks.slice(0, 10).map((task) => ({
    id: task.id,
    title: task.name || task.title || 'Task',
    meta: task.due_date ? moment(Number(task.due_date)).format('MMM D') : 'ClickUp',
    status: task.status?.status || task.status || 'Open',
  }));

  const dashboardDetailContent = (() => {
    if (activeDashboardSection === DASHBOARD_SECTIONS.attendance) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setCalendarMonth(prev => moment(prev).subtract(1, 'month'))}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700"
            >
              Prev
            </button>
            <div className="text-center text-base font-black text-slate-950">
              {moment(calendarMonth).format('MMMM YYYY')}
            </div>
            <button
              type="button"
              onClick={() => setCalendarMonth(prev => moment(prev).add(1, 'month'))}
              disabled={moment(calendarMonth).add(1, 'month').isAfter(moment(), 'month')}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700 disabled:opacity-40"
            >
              Next
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs font-semibold text-slate-600">
            <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-green-500" />Present</span>
            <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-purple-500" />Leave</span>
            <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" />Holiday</span>
            <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-orange-500" />Checkout missing</span>
            <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-red-500" />Missing</span>
          </div>

          {calendarLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-2">
              <div className="grid grid-cols-7 gap-1 pb-2 text-center text-[10px] font-black uppercase text-slate-400">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                  <div key={`${day}-${index}`}>{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendarWeeks.flat().map((date) => {
                  const isCurrentMonth = date.isSame(calendarMonth, 'month');
                  const isToday = date.isSame(moment(), 'day');
                  const dayState = getCalendarDayState(date);
                  const status = getCompactCalendarStatus(dayState);
                  const canOpenAction = dayState.hasMissingCheckout || dayState.hasMissingAttendance;

                  return (
                    <button
                      key={dayState.dateKey}
                      type="button"
                      disabled={!canOpenAction}
                      onClick={() => {
                        closeDashboardDetail();
                        handleOpenCorrectionModal(date.toDate());
                      }}
                      className={`flex aspect-square min-h-[42px] flex-col items-center justify-center rounded-xl border bg-white text-xs font-black shadow-sm ${
                        isToday ? 'border-blue-500 text-blue-700' : 'border-slate-200'
                      } ${!isCurrentMonth ? 'opacity-35' : ''} ${canOpenAction ? 'hover:border-blue-300' : ''}`}
                      title={status.label || date.format('MMM D')}
                    >
                      <span>{date.format('D')}</span>
                      <span className={`mt-1 h-2 w-2 rounded-full ${status.dotClass}`} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-black text-slate-950">Recent attendance logs</div>
              <div className="mt-3 space-y-2">
                {recentAttendanceLogs.length === 0 ? (
                  <div className="text-xs font-medium text-slate-500">No recent punch logs.</div>
                ) : recentAttendanceLogs.slice(0, 5).map((log, index) => (
                  <div key={`${log.logTime}-${index}`} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2">
                    <span className="text-xs font-bold text-slate-700">{log.punchLogType}</span>
                    <span className="text-xs font-medium text-slate-500">{moment(log.logTime).format('MMM D, h:mm A')}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-black text-slate-950">Daily hours breakdown</div>
              <div className="mt-3 space-y-2">
                {attendanceData?.dailyHours?.length ? attendanceData.dailyHours.slice(0, 5).map((day) => {
                  const totalHours = Number(day.hoursWorked || 0) + Number(day.extraHours || 0);
                  return (
                    <div key={day.date} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2">
                      <span className="text-xs font-bold text-slate-700">{moment(day.date).format('MMM D')}</span>
                      <span className="text-xs font-medium text-slate-500">
                        {formatLimitValue(totalHours)}h
                        {day.extraHours > 0 ? ` (${formatLimitValue(day.extraHours)} extra)` : ''}
                      </span>
                    </div>
                  );
                }) : (
                  <div className="text-xs font-medium text-slate-500">No daily hours yet.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeDashboardSection === DASHBOARD_SECTIONS.requests || activeDashboardSection === DASHBOARD_SECTIONS.leaves) {
      const items = activeDashboardSection === DASHBOARD_SECTIONS.leaves
        ? dashboardRequestItems.filter((item) => item.id.startsWith('leave-'))
        : dashboardRequestItems;

      return (
        <div className="space-y-3">
          {items.length === 0 ? (
            <div className="rounded-2xl bg-slate-50 p-5 text-sm font-medium text-slate-500">No requests found.</div>
          ) : items.slice(0, 12).map((item) => (
            <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-black text-slate-950">{item.title}</div>
                  <div className="mt-1 text-xs font-medium text-slate-500">{item.meta}</div>
                </div>
                <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-black capitalize text-slate-700">
                  {item.status || 'open'}
                </span>
              </div>
              {item.note && <div className="mt-2 line-clamp-2 text-xs text-slate-600">{item.note}</div>}
            </div>
          ))}
        </div>
      );
    }

    if (activeDashboardSection === DASHBOARD_SECTIONS.tasks) {
      return (
        <div className="space-y-3">
          {dashboardTaskItems.length === 0 ? (
            <div className="rounded-2xl bg-slate-50 p-5 text-sm font-medium text-slate-500">No open tasks.</div>
          ) : dashboardTaskItems.map((task) => (
            <div key={task.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-black text-slate-950">{task.title}</div>
              <div className="mt-1 flex items-center justify-between gap-3 text-xs font-medium text-slate-500">
                <span>{task.meta}</span>
                <span className="rounded-full bg-blue-50 px-2.5 py-1 font-black text-blue-700">{task.status}</span>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (activeDashboardSection === DASHBOARD_SECTIONS.holidays) {
      const holidayDetailItems = getHolidayDetailItems({
        showAll: showAllHolidayDetails,
        currentMonthHolidays,
        upcomingHolidays,
      });

      return (
        <div className="space-y-3">
          <div className="rounded-2xl bg-amber-50 p-4 text-sm font-bold text-amber-800">
            {showAllHolidayDetails
              ? `All upcoming holidays: ${upcomingHolidays.length}`
              : `Upcoming holidays in ${moment().format('MMMM')}: ${currentMonthHolidays.length}`}
          </div>
          {!showAllHolidayDetails && upcomingHolidays.length > currentMonthHolidays.length && (
            <button
              type="button"
              onClick={() => setShowAllHolidayDetails(true)}
              className="w-full rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm font-black text-amber-700 shadow-sm hover:bg-amber-50"
            >
              List all holidays
            </button>
          )}
          {showAllHolidayDetails && (
            <button
              type="button"
              onClick={() => setShowAllHolidayDetails(false)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Show this month only
            </button>
          )}
          {holidayDetailItems.length === 0 ? (
            <div className="rounded-2xl bg-slate-50 p-5 text-sm font-medium text-slate-500">
              {showAllHolidayDetails ? 'No upcoming holidays.' : 'No upcoming holidays in this month.'}
            </div>
          ) : holidayDetailItems.map((holiday) => (
            <div key={holiday.id} className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
              <div className="text-sm font-black text-slate-950">{holiday.holidayName}</div>
              <div className="mt-1 text-xs font-bold text-amber-700">{moment(holiday.holidayDate).format('dddd, MMMM D')}</div>
            </div>
          ))}
        </div>
      );
    }

    if (activeDashboardSection === DASHBOARD_SECTIONS.performance) {
      const summary = performanceData?.summary || {};
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2 rounded-2xl bg-slate-100 p-1">
            {[7, 14, 30].map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => setPerformanceDays(days)}
                className={`rounded-xl px-3 py-2 text-xs font-black transition ${
                  performanceDays === days ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'
                }`}
              >
                {days} days
              </button>
            ))}
          </div>

          {performanceLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {[
                ['Completed', summary.totalCompleted || 0],
                ['New tasks', summary.totalNewTasks || 0],
                ['Avg/day', summary.avgDailyTasks || 0],
                ['Delta', (summary.totalNewTasks || 0) - (summary.totalCompleted || 0)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-2xl font-black text-slate-950">{value}</div>
                  <div className="mt-1 text-xs font-bold text-slate-500">{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return null;
  })();

  const showAttendanceDetail = false;
  const showRequestDetail = false;
  const showTaskDetail = false;
  const showHolidayDetail = false;
  const showPerformanceDetail = false;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-gray-600">Loading your attendance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <button
            onClick={() => loadAttendanceData(user)}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div
      ref={pullToRefresh.containerRef}
      className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pb-6"
    >
      {pullToRefresh.isEnabled && (
        <div className="sticky top-0 z-40 px-3 pt-3 sm:px-6 lg:px-8 pointer-events-none">
          <div
            className="mx-auto max-w-md overflow-hidden rounded-2xl border border-blue-200/80 bg-white/90 shadow-lg backdrop-blur"
            style={{
              height: `${pullToRefresh.indicatorHeight}px`,
              opacity: pullToRefresh.indicatorHeight > 0 ? 1 : 0,
              transition: pullToRefresh.indicatorTransition,
            }}
          >
            <div className="flex h-14 items-center justify-center gap-3 text-sm font-semibold text-slate-700">
              <div
                className={`h-5 w-5 rounded-full border-2 border-blue-500 border-t-transparent ${
                  pullToRefresh.isRefreshing ? 'animate-spin' : ''
                }`}
              />
              <span>
                {pullToRefresh.isRefreshing
                  ? 'Refreshing dashboard...'
                  : pullToRefresh.progress >= 1
                    ? 'Release to refresh'
                    : 'Pull to refresh'}
              </span>
            </div>
          </div>
        </div>
      )}
      <EmployeeDashboardStatsHub
        user={user}
        stats={dashboardStats}
        activeSection={activeDashboardSection}
        onSectionChange={openDashboardDetail}
        onApplyLeave={() => setShowLeaveModal(true)}
        onRequestCheckout={() => openCheckoutRequestModal()}
        onRequestExtraHours={() => setShowExtraHoursModal(true)}
        onProfilePhotoEdit={() => setShowProfilePhotoUpload(true)}
        headerActions={headerActions}
        todayCard={todayCard}
        eligibleDatesCount={eligibleDates.length}
      />
      <EmployeeDashboardDetailSheet
        isOpen={Boolean(activeDashboardSection)}
        title={selectedDashboardStat?.label || 'Details'}
        summary={selectedDashboardStat ? `${selectedDashboardStat.value} - ${selectedDashboardStat.meta}` : ''}
        onClose={closeDashboardDetail}
      >
        {dashboardDetailContent}
      </EmployeeDashboardDetailSheet>

      {/* Header - Professional Design with Animations */}
      <div className="hidden bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900 shadow-professional-lg animate-fade-in">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-5 sm:py-7 gap-4">
            <div className="flex items-center w-full sm:w-auto animate-slide-in-right">
              <div className="flex-shrink-0 relative">
                <div className="h-14 w-14 sm:h-18 sm:w-18 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-professional-lg hover-lift transition-smooth ring-2 ring-white/20 overflow-hidden">
                  {user?.profilePhotoUrl ? (
                    <img
                      src={user.profilePhotoUrl}
                      alt={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User'}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <UserCircleIcon className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                  )}
                </div>
                <div className="absolute -bottom-1 -right-1 h-5 w-5 bg-green-500 rounded-full border-2 border-white shadow-professional animate-pulse"></div>
              </div>
              <div className="ml-4 sm:ml-6 flex-1">
                <h1 className="text-xl sm:text-3xl font-bold text-white drop-shadow-lg tracking-tight">
                  Welcome, {user?.firstName} {user?.lastName}!
                </h1>
                <p className="text-sm sm:text-base text-slate-200 mt-1.5 font-medium flex items-center gap-2">
                  <span className="inline-block w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  {user?.department} • Your Dashboard
                </p>
              </div>
            </div>
            <div className="flex items-center flex-wrap gap-2 animate-fade-in stagger-1 bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl px-3 py-2 shadow-professional">
              <button
                onClick={() => setShowProfileEdit(true)}
                className="group inline-flex items-center px-3 sm:px-4 py-2 sm:py-2.5 border border-white/15 text-sm font-semibold rounded-xl text-white bg-white/5 hover:bg-white/20 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-smooth"
                title="Edit Profile"
              >
                <PencilIcon className="h-5 w-5 sm:mr-2 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">Edit Profile</span>
              </button>
              <button
                onClick={() => navigate('/my-kpi')}
                className="group inline-flex items-center px-3 sm:px-4 py-2 sm:py-2.5 border border-white/15 text-sm font-semibold rounded-xl text-white bg-white/5 hover:bg-white/20 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-smooth"
                title="My KPI"
              >
                <ChartBarIcon className="h-5 w-5 sm:mr-2 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">My KPI</span>
              </button>
              <button
                onClick={() => navigate('/my-feedback')}
                className={`group relative inline-flex items-center px-3 sm:px-4 py-2 sm:py-2.5 border text-sm font-semibold rounded-xl text-white hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-smooth ${
                  unreadRepliesCount > 0 
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 border-green-500/50 hover:from-green-700 hover:to-emerald-700' 
                    : 'bg-white/5 border-white/15 hover:bg-white/20'
                }`}
                title={unreadRepliesCount > 0 ? `${unreadRepliesCount} unread repl${unreadRepliesCount > 1 ? 'ies' : 'y'}` : 'My Feedback'}
              >
                <ChatBubbleLeftRightIcon className="h-5 w-5 sm:mr-2 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">My Feedback</span>
                {unreadRepliesCount > 0 && (
                  <span className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white shadow-professional-lg animate-pulse border-2 border-white">
                    {unreadRepliesCount > 99 ? '99+' : unreadRepliesCount}
                  </span>
                )}
              </button>
              <DashboardSwitcher />
              <NotificationBell />
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-3 sm:px-4 py-2 sm:py-2.5 border border-white/15 text-sm font-semibold rounded-xl text-white bg-white/5 hover:bg-red-600/80 hover:border-red-500/50 focus:outline-none focus:ring-2 focus:ring-red-400/50 transition-smooth self-end sm:self-auto"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 sm:py-8 px-3 sm:px-6 lg:px-8">
        {deviceLock?.attendanceBlocked && (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-800 shadow-sm">
            This phone is locked for attendance use by <strong>{deviceLock.lockedEmail}</strong>.
            {deviceLock.pendingRequest ? ' Your device unlock request is pending review.' : ' Request an unlock to use attendance with this account.'}
          </div>
        )}
        {deviceRegistration?.registered === false && (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900 shadow-sm">
            <p>{deviceRegistration.message || 'This phone is not your registered attendance device.'}</p>
            {deviceChangePending ? (
              <p className="mt-2 font-medium">Your device change request is pending review.</p>
            ) : (
              <button
                type="button"
                onClick={handleRequestDeviceChange}
                className="mt-2 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Request device change
              </button>
            )}
          </div>
        )}
        {/* Two Column Layout: Attendance and Quick Actions */}
        <div className="hidden grid-cols-1 lg:grid-cols-2 gap-6 mb-6 sm:mb-7 animate-fade-in-up">
          {/* Left: Attendance Card */}
          <div>
            <CheckInWidget
              refreshSignal={checkInWidgetRefreshToken}
              deviceLock={deviceLock}
              onRequestUnlock={handleRequestDeviceUnlock}
            />
          </div>

          {/* Right: Quick Actions Card */}
          <div className="bg-white rounded-2xl shadow-professional-lg p-6 border border-gray-200">
            {/* Title Section */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900">Quick Actions</h2>
            </div>

            {/* Apply for Leave Button */}
            <button
              onClick={() => setShowLeaveModal(true)}
              className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-300 shadow-professional hover:shadow-professional-lg mb-4 font-semibold"
            >
              <CalendarDaysIcon className="w-5 h-5 mr-2" />
              Apply for Leave
            </button>

            {/* Request Checkout Time Button */}
            <button
              onClick={() => openCheckoutRequestModal()}
              className="relative w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl hover:from-orange-600 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-300 shadow-professional hover:shadow-professional-lg mb-4 font-semibold"
            >
              <ClockIcon className="w-5 h-5 mr-2" />
              Request Checkout Time
              {eligibleDates.length > 0 && (
                <span className="absolute -top-2 -right-2 flex items-center justify-center w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full shadow-professional animate-pulse">
                  {eligibleDates.length}
                </span>
              )}
            </button>

            {/* Request Extra Hours Button */}
            <button
              onClick={() => setShowExtraHoursModal(true)}
              className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl hover:from-blue-600 hover:to-cyan-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-300 shadow-professional hover:shadow-professional-lg font-semibold"
            >
              <ClockIcon className="w-5 h-5 mr-2" />
              Request Extra Hours
            </button>

            {/* Info Message */}
            {eligibleDates.length > 0 && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start">
                <svg className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <p className="text-sm text-gray-700">
                  You have {eligibleDates.length} date{eligibleDates.length > 1 ? 's' : ''} that need checkout time
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Attendance & Leave Calendar */}
        {showAttendanceDetail && (
        <div className="mb-6 sm:mb-7 animate-fade-in-up">
          <div className="bg-white rounded-2xl shadow-professional-lg p-6 border border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Attendance & Leave Calendar</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Tap an incomplete day to request checkout or correction
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCalendarMonth(prev => moment(prev).subtract(1, 'month'))}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Prev
                </button>
                <div className="text-sm font-semibold text-gray-900 min-w-[140px] text-center">
                  {moment(calendarMonth).format('MMMM YYYY')}
                </div>
                <button
                  onClick={() => setCalendarMonth(prev => moment(prev).add(1, 'month'))}
                  disabled={moment(calendarMonth).add(1, 'month').isAfter(moment(), 'month')}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-4">
              <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-green-500"></span>Present</span>
              <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-purple-500"></span>Leave</span>
              <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-amber-500"></span>Holiday</span>
              <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-orange-500"></span>Checkout Missing</span>
              <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-500"></span>Missing</span>
            </div>

            {calendarLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-10 w-10 border-4 border-gray-200 border-t-blue-600 rounded-full"></div>
              </div>
            ) : (
              <>
                {/* Mobile list view */}
                <div className="sm:hidden space-y-3">
                  {calendarDays.map((date) => {
                    const dayState = getCalendarDayState(date);
                    const {
                      dateKey,
                      attendance,
                      holiday,
                      leaveEntries,
                      correctionRequest,
                      hasActiveCheckoutRequest,
                      totalHours,
                      hasAttendance,
                      hasMissingCheckout,
                      hasMissingAttendance
                    } = dayState;
                    const canRequestCheckout = hasMissingCheckout && !hasActiveCheckoutRequest && !correctionRequest;
                    const canRequestCorrection = hasMissingAttendance && !correctionRequest;

                    let statusLabel = '';
                    let statusClass = '';
                    let subLabel = '';

                    if (holiday) {
                      statusLabel = 'Holiday';
                      statusClass = 'bg-amber-100 text-amber-800';
                      subLabel = holiday.holidayName || '';
                    } else if (leaveEntries.length > 0) {
                      statusLabel = 'Leave';
                      statusClass = 'bg-purple-100 text-purple-800';
                      subLabel = leaveEntries[0]?.leaveType || '';
                    } else if (correctionRequest?.status === 'pending') {
                      statusLabel = 'Correction Pending';
                      statusClass = 'bg-blue-100 text-blue-800';
                      subLabel = 'Awaiting review';
                    } else if (hasActiveCheckoutRequest) {
                      statusLabel = 'Checkout Pending';
                      statusClass = 'bg-orange-100 text-orange-800';
                      subLabel = 'Awaiting review';
                    } else if (hasAttendance && (totalHours > 0 || attendance?.lastCheckOut)) {
                      statusLabel = 'Present';
                      statusClass = 'bg-green-100 text-green-800';
                      subLabel = `${formatLimitValue(totalHours)}h logged`;
                    } else if (hasMissingCheckout) {
                      statusLabel = 'Checkout Missing';
                      statusClass = 'bg-orange-100 text-orange-800';
                      subLabel = attendance?.firstCheckIn
                        ? `Checked in at ${moment(attendance.firstCheckIn).format('h:mm A')}`
                        : 'Check-out time is missing';
                    } else if (hasMissingAttendance) {
                      statusLabel = 'Missing';
                      statusClass = 'bg-red-100 text-red-800';
                      subLabel = 'No attendance found';
                    }

                    return (
                      <div key={dateKey} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {date.format('ddd, MMM D')}
                            </div>
                            <div className="text-xs text-gray-500">{date.format('YYYY')}</div>
                          </div>
                          {statusLabel && (
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusClass}`}>
                              {statusLabel}
                            </span>
                          )}
                        </div>
                        {subLabel && (
                          <div className="mt-2 text-xs text-gray-600">{subLabel}</div>
                        )}
                        {correctionRequest?.status === 'rejected' && (
                          <div className="mt-2 text-xs text-red-600">Correction Rejected</div>
                        )}
                        {canRequestCheckout && (
                          <button
                            onClick={() => handleOpenCorrectionModal(date.toDate())}
                            className="mt-3 w-full text-xs font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-md py-2 hover:bg-orange-100 transition-colors"
                          >
                            Request Checkout
                          </button>
                        )}
                        {canRequestCorrection && (
                          <button
                            onClick={() => handleOpenCorrectionModal(date.toDate())}
                            className="mt-3 w-full text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-md py-2 hover:bg-red-100 transition-colors"
                          >
                            Request Correction
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Desktop grid view */}
                <div className="hidden sm:block border border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
                <div className="grid grid-cols-7 bg-gray-100 border-b border-gray-200 min-w-[700px]">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                    <div key={day} className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase border-r border-gray-200 last:border-r-0">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="min-w-[700px]">
                  {calendarWeeks.map((week, weekIndex) => (
                    <div key={weekIndex} className="grid grid-cols-7 border-b border-gray-200 last:border-b-0">
                      {week.map((date) => {
                        const isCurrentMonth = date.isSame(calendarMonth, 'month');
                        const isToday = date.isSame(moment(), 'day');
                        const dayState = getCalendarDayState(date);
                        const {
                          dateKey,
                          attendance,
                          holiday,
                          leaveEntries,
                          correctionRequest,
                          hasActiveCheckoutRequest,
                          totalHours,
                          hasAttendance,
                          hasMissingCheckout,
                          hasMissingAttendance
                        } = dayState;
                        const canRequestCheckout = hasMissingCheckout && !hasActiveCheckoutRequest && !correctionRequest;
                        const canRequestCorrection = hasMissingAttendance && !correctionRequest;

                        let statusLabel = '';
                        let statusClass = '';

                        if (holiday) {
                          statusLabel = holiday.holidayName || 'Holiday';
                          statusClass = 'bg-amber-100 text-amber-800';
                        } else if (leaveEntries.length > 0) {
                          statusLabel = leaveEntries[0]?.leaveType || 'Leave';
                          statusClass = 'bg-purple-100 text-purple-800';
                        } else if (correctionRequest?.status === 'pending') {
                          statusLabel = 'Correction Pending';
                          statusClass = 'bg-blue-100 text-blue-800';
                        } else if (hasActiveCheckoutRequest) {
                          statusLabel = 'Checkout Pending';
                          statusClass = 'bg-orange-100 text-orange-800';
                        } else if (hasAttendance && (totalHours > 0 || attendance?.lastCheckOut)) {
                          statusLabel = `${formatLimitValue(totalHours)}h`;
                          statusClass = 'bg-green-100 text-green-800';
                        } else if (hasMissingCheckout) {
                          statusLabel = 'Checkout Missing';
                          statusClass = 'bg-orange-100 text-orange-800';
                        } else if (hasMissingAttendance) {
                          statusLabel = 'Missing';
                          statusClass = 'bg-red-100 text-red-800';
                        }

                        return (
                          <div
                            key={dateKey}
                            className={`min-h-[110px] border-r border-gray-200 last:border-r-0 p-2 ${
                              isCurrentMonth ? 'bg-white' : 'bg-gray-50 text-gray-400'
                            } ${isToday ? 'ring-2 ring-blue-400' : ''}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold">{date.format('D')}</span>
                              {isToday && <span className="text-[10px] text-blue-600 font-semibold">Today</span>}
                            </div>
                            {statusLabel && (
                              <div className={`text-[11px] font-semibold px-2 py-1 rounded-full inline-flex items-center ${statusClass}`}>
                                {statusLabel}
                                {leaveEntries.length > 1 && (
                                  <span className="ml-1">+{leaveEntries.length - 1}</span>
                                )}
                              </div>
                            )}
                            {canRequestCheckout && (
                              <button
                                onClick={() => handleOpenCorrectionModal(date.toDate())}
                                className="mt-2 w-full text-xs font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-md py-1 hover:bg-orange-100 transition-colors"
                              >
                                Request Checkout
                              </button>
                            )}
                            {canRequestCorrection && (
                              <button
                                onClick={() => handleOpenCorrectionModal(date.toDate())}
                                className="mt-2 w-full text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-md py-1 hover:bg-red-100 transition-colors"
                              >
                                Request Correction
                              </button>
                            )}
                            {correctionRequest?.status === 'rejected' && (
                              <div className="mt-2 text-[11px] text-red-600">Correction Rejected</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
                </div>
              </>
            )}
          </div>
        </div>
        )}

        {/* My Leave Requests, Checkout Requests, and Extra Hours Requests - Three Column Layout */}
        {showRequestDetail && (myLeaveRequests.length > 0 || myRequests.length > 0 || myExtraHoursRequests.length > 0) && (
          <div className={`grid grid-cols-1 ${myLeaveRequests.length > 0 && myRequests.length > 0 && myExtraHoursRequests.length > 0 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-6 mb-5 sm:mb-7`}>
            {/* My Leave Requests Section - Left */}
            {myLeaveRequests.length > 0 && (
              <div className="bg-white shadow-professional rounded-2xl p-6 border border-gray-200 animate-fade-in-up">
                <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center">
                  <CalendarDaysIcon className="h-5 w-5 text-purple-600 mr-2" />
                  My Leave Requests
                </h3>
                <div className="space-y-4">
                  {myLeaveRequests.map((req) => {
                    // Determine leave type color
                    const getLeaveTypeColor = (type) => {
                      if (type.includes('Casual')) return 'bg-blue-100 text-blue-800';
                      if (type.includes('Sick')) return 'bg-red-100 text-red-800';
                      if (type.includes('Earned')) return 'bg-purple-100 text-purple-800';
                      return 'bg-gray-100 text-gray-800';
                    };

                    return (
                      <div key={req.id} className="bg-white rounded-lg border border-gray-200 p-4 relative hover:shadow-md transition-shadow">
                        {/* Time ago at top right */}
                        <div className="absolute top-4 right-4 text-xs text-gray-400">
                          {moment(req.createdAt).fromNow()}
                        </div>

                        {/* Tags Row */}
                        <div className="flex items-center justify-between gap-2 mb-3 pr-16">
                          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                            <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getLeaveTypeColor(req.leaveType)}`}>
                              {req.leaveType}
                            </span>
                            <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                              req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              req.status === 'approved' ? 'bg-green-100 text-green-800' :
                              req.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                            </span>
                          </div>
                          {req.status && req.status.toLowerCase() === 'pending' && (
                            <button
                              onClick={() => handleCancelLeaveRequest(req.id)}
                              className="px-3 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors shadow-sm flex-shrink-0 whitespace-nowrap"
                              title="Cancel request"
                            >
                              Cancel
                            </button>
                          )}
                        </div>

                        {/* Date with Calendar Icon */}
                        <div className="flex items-center mb-3">
                          <CalendarDaysIcon className="w-4 h-4 text-gray-500 mr-2" />
                          <span className="text-sm text-gray-700">
                            {moment(req.startDate).format('MMM D')} - {moment(req.endDate).format('MMM D')} ({req.totalDays} day{req.totalDays > 1 ? 's' : ''})
                          </span>
                        </div>

                        {/* Reason */}
                        {req.reason && (
                          <p className="text-sm text-gray-700 mb-3 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{req.reason}</p>
                        )}

                        {/* Manager Notes - Distinct Box */}
                        {req.reviewerNotes && (
                          <div className="mt-3 pt-3 border-t border-gray-200 bg-gray-50 rounded-lg p-3">
                            <p className="text-sm text-gray-700 italic">{req.reviewerNotes}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* My Checkout Requests Section - Right */}
            {myRequests.length > 0 && (
              <div className="bg-white shadow-professional rounded-2xl p-6 border border-gray-200 animate-fade-in-up">
                <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center">
                  <ClockIcon className="h-5 w-5 text-orange-600 mr-2" />
                  My Checkout Requests
                </h3>
                <div className="space-y-4">
                  {myRequests.slice(0, 5).map((req) => (
                    <div key={req.id} className="bg-white rounded-lg border border-gray-200 p-4 relative hover:shadow-md transition-shadow">
                      {/* Time ago at top right */}
                      <div className="absolute top-4 right-4 text-xs text-gray-400">
                        {moment(req.createdAt).fromNow()}
                      </div>

                      {/* Status Tag */}
                      <div className="flex items-center justify-between gap-2 mb-3 pr-16">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                          req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          req.status === 'approved' ? 'bg-green-100 text-green-800' :
                          req.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                        </span>
                        {req.status && req.status.toLowerCase() === 'pending' && (
                          <button
                            onClick={() => handleCancelCheckoutRequest(req.id)}
                            className="px-3 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors shadow-sm flex-shrink-0 whitespace-nowrap"
                            title="Cancel request"
                          >
                            Cancel
                          </button>
                        )}
                      </div>

                      {/* Date and Time with Clock Icon */}
                      <div className="flex items-center mb-3">
                        <ClockIcon className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-sm text-gray-700">
                          {moment(req.requestDate).format('ddd, MMM D, YYYY')} @ {moment(req.requestedCheckoutTime).format('h:mm A')}
                        </span>
                      </div>

                      {/* Reason */}
                      {req.reason && (
                        <p className="text-sm text-gray-700 mb-3 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{req.reason}</p>
                      )}

                      {/* Manager Notes - Distinct Box */}
                      {req.reviewerNotes && (
                        <div className="mt-3 pt-3 border-t border-gray-200 bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-700 italic">{req.reviewerNotes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                  {myRequests.length > 5 && (
                    <p className="text-xs text-gray-500 text-center pt-2">
                      Showing latest 5 of {myRequests.length} requests
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* My Extra Hours Requests Section */}
            {myExtraHoursRequests.length > 0 && (
              <div className="bg-white shadow-professional rounded-2xl p-6 border border-gray-200 animate-fade-in-up">
                <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center">
                  <ClockIcon className="h-5 w-5 text-blue-600 mr-2" />
                  My Extra Hours Requests
                </h3>
                <div className="space-y-4">
                  {myExtraHoursRequests.slice(0, 5).map((req) => (
                    <div key={req.id} className="bg-white rounded-lg border border-gray-200 p-4 relative hover:shadow-md transition-shadow">
                      {/* Time ago at top right */}
                      <div className="absolute top-4 right-4 text-xs text-gray-400">
                        {moment(req.createdAt).fromNow()}
                      </div>

                      {/* Status Tag */}
                      <div className="flex items-center justify-between gap-2 mb-3 pr-16">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                          req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          req.status === 'approved' ? 'bg-green-100 text-green-800' :
                          req.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                        </span>
                        {req.status && req.status.toLowerCase() === 'pending' && (
                          <button
                            onClick={() => handleCancelExtraHoursRequest(req.id)}
                            className="px-3 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors shadow-sm flex-shrink-0 whitespace-nowrap"
                            title="Cancel request"
                          >
                            Cancel
                          </button>
                        )}
                      </div>

                      {/* Date and Hours with Clock Icon */}
                      <div className="flex items-center mb-3">
                        <ClockIcon className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-sm text-gray-700">
                          {moment(req.requestDate).format('ddd, MMM D, YYYY')} - {req.requestedHours} hour(s) requested
                        </span>
                      </div>

                      {/* Approved Hours (if approved/partial) */}
                      {req.status === 'approved' && req.approvedHours !== null && (
                        <div className="flex items-center mb-3 text-sm">
                          <CheckCircleIcon className="w-4 h-4 text-green-600 mr-2" />
                          <span className="text-gray-700">
                            {req.approvedHours} hour(s) approved
                            {req.approvedHours < req.requestedHours && (
                              <span className="text-orange-600 ml-1">(Partial approval)</span>
                            )}
                          </span>
                        </div>
                      )}

                      {/* Reason */}
                      {req.reason && (
                        <p className="text-sm text-gray-700 mb-3 break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{req.reason}</p>
                      )}

                      {/* Manager Notes - Distinct Box */}
                      {req.reviewerNotes && (
                        <div className="mt-3 pt-3 border-t border-gray-200 bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-700 italic">{req.reviewerNotes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                  {myExtraHoursRequests.length > 5 && (
                    <p className="text-xs text-gray-500 text-center pt-2">
                      Showing latest 5 of {myExtraHoursRequests.length} requests
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* My Workload Timeline Section */}
        {showTaskDetail && (
        <div className="mb-7 bg-white shadow-professional-lg rounded-2xl p-5 sm:p-7 border border-teal-100 overflow-hidden animate-fade-in-up">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center mb-2">
                <div className="mr-3 p-2 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl shadow-professional">
                  <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                My Task Timeline
              </h3>
              <p className="text-sm text-gray-600 ml-12">View and manage your task schedule</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {showWorkloadSection && workloadData && workloadData.tasks && workloadData.tasks.length > 0 && (
                <>
                  <select
                    value={timelineDays}
                    onChange={(e) => setTimelineDays(parseInt(e.target.value))}
                    className="px-2 sm:px-3 py-2 border-2 border-gray-200 rounded-lg text-xs sm:text-sm font-medium focus:ring-2 focus:ring-teal-500"
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={21}>21 days</option>
                    <option value={30}>30 days</option>
                  </select>
                  
                  <div className="flex items-center gap-1 border-2 border-gray-200 rounded-lg p-1">
                    <button
                      onClick={() => setTimelineStartDate(moment(timelineStartDate).subtract(timelineDays, 'days').toDate())}
                      className="px-2 py-1 hover:bg-gray-100 rounded"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setTimelineStartDate(moment().startOf('week').toDate())}
                      className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded"
                    >
                      Today
                    </button>
                    <button
                      onClick={() => setTimelineStartDate(moment(timelineStartDate).add(timelineDays, 'days').toDate())}
                      className="px-2 py-1 hover:bg-gray-100 rounded"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </>
              )}
              
              <button
                onClick={() => {
                  if (!showWorkloadSection) setShowWorkloadSection(true);
                  loadWorkloadData();
                }}
                disabled={workloadLoading}
                className="inline-flex items-center px-4 sm:px-5 py-2.5 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 disabled:opacity-50 text-xs sm:text-sm font-semibold shadow-professional hover:shadow-professional-lg hover:scale-[1.02]"
              >
                <svg className={`w-4 h-4 mr-1 sm:mr-2 ${workloadLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {showWorkloadSection && workloadData ? 'Refresh' : 'Load Timeline'}
              </button>
              
              {showWorkloadSection && (
                <button
                  onClick={() => setShowWorkloadSection(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {showWorkloadSection && (
            <>
              {workloadLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="animate-spin h-16 w-16 border-4 border-teal-200 border-t-teal-600 rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading your tasks...</p>
                  </div>
                </div>
              ) : !workloadData ? (
                <div className="text-center py-20">
                  <ClockIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">Click "Load Timeline" to view your task schedule</p>
                </div>
              ) : workloadData.error ? (
                <div className="text-center py-20">
                  <ExclamationTriangleIcon className="h-16 w-16 text-orange-400 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">{workloadData.error}</p>
                </div>
              ) : workloadData.tasks && workloadData.tasks.length === 0 ? (
                <div className="text-center py-20">
                  <CheckCircleIcon className="h-16 w-16 text-green-400 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">No tasks assigned</p>
                  <p className="text-gray-400 text-sm mt-2">You're all clear!</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    {/* Task Summary */}
                    <div className="mb-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-5 border-2 border-teal-200 card-hover shadow-professional">
                        <div className="text-xs font-semibold text-teal-900 mb-2 uppercase tracking-wide">Total Tasks</div>
                        <div className="text-3xl font-bold text-teal-700">{workloadData.taskCount || 0}</div>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border-2 border-blue-200 card-hover shadow-professional">
                        <div className="text-xs font-semibold text-blue-900 mb-2 uppercase tracking-wide">With Dates</div>
                        <div className="text-3xl font-bold text-blue-700">
                          {workloadData.tasks?.filter(t => t.start_date && t.due_date).length || 0}
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border-2 border-purple-200 card-hover shadow-professional">
                        <div className="text-xs font-semibold text-purple-900 mb-2 uppercase tracking-wide">Your Department</div>
                        <div className="text-base font-bold text-purple-700 mt-2">{workloadData.department || 'N/A'}</div>
                      </div>
                    </div>

                    {/* Timeline View - Calendar Grid Layout */}
                    <div className="relative -mx-4 sm:-mx-6">
                      <div className="overflow-x-auto overflow-y-hidden pb-4 px-4 sm:px-6 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                      {(() => {
                        // Create a map of dates to tasks
                        const tasksByDate = {};
                        
                        // Initialize all dates in the range
                        for (let i = 0; i < timelineDays; i++) {
                          const currentDate = moment(timelineStartDate).add(i, 'days');
                          const dateKey = currentDate.format('YYYY-MM-DD');
                          tasksByDate[dateKey] = {
                            date: currentDate,
                            tasks: []
                          };
                        }
                        
                        // Add tasks to their dates
                        workloadData.tasks.forEach(task => {
                          const taskStart = task.start_date ? moment(parseInt(task.start_date)) : null;
                          const taskEnd = task.due_date ? moment(parseInt(task.due_date)) : null;
                          
                          if (taskStart && taskEnd) {
                            for (let i = 0; i < timelineDays; i++) {
                              const currentDate = moment(timelineStartDate).add(i, 'days');
                              if (currentDate.isBetween(taskStart, taskEnd, 'day', '[]')) {
                                const dateKey = currentDate.format('YYYY-MM-DD');
                                tasksByDate[dateKey].tasks.push({
                                  ...task,
                                  isStart: currentDate.isSame(taskStart, 'day'),
                                  isEnd: currentDate.isSame(taskEnd, 'day'),
                                  startDate: taskStart,
                                  endDate: taskEnd
                                });
                              }
                            }
                          }
                        });
                        
                        const sortedDates = Object.keys(tasksByDate).sort();
                        
                        return (
                          <div className="border-2 border-gray-200 rounded-xl overflow-hidden inline-block min-w-full shadow-professional">
                            {/* Calendar Grid */}
                            <div className="flex" style={{ minWidth: 'fit-content' }}>
                            {sortedDates.map(dateKey => {
                              const { date, tasks } = tasksByDate[dateKey];
                              const isToday = date.isSame(moment(), 'day');
                              const isWeekend = date.day() === 0 || date.day() === 6;
                              
                              return (
                                <div 
                                  key={dateKey} 
                                  className={`flex-shrink-0 border-r border-gray-300 last:border-r-0 min-h-[400px] w-[200px] sm:w-[240px] md:w-[280px] ${
                                    isWeekend ? 'bg-gray-50' : 'bg-white'
                                  }`}
                                >
                                  {/* Date Header */}
                                  <div className={`sticky top-0 z-10 px-2 sm:px-4 py-3 sm:py-4 border-b-2 ${
                                    isToday 
                                      ? 'bg-gradient-to-b from-teal-500 to-teal-600 border-teal-700' 
                                      : isWeekend 
                                      ? 'bg-gradient-to-b from-gray-400 to-gray-500 border-gray-600'
                                      : 'bg-gradient-to-b from-blue-500 to-blue-600 border-blue-700'
                                  }`}>
                                    <div className="text-center">
                                      <div className="text-white text-[10px] sm:text-xs font-bold uppercase tracking-wider mb-1">
                                        {date.format('ddd')}
                                      </div>
                                      <div className="text-white text-2xl sm:text-3xl font-bold mb-1">
                                        {date.format('D')}
                                      </div>
                                      <div className="text-white text-xs sm:text-sm font-medium opacity-90">
                                        {date.format('MMM YYYY')}
                                      </div>
                                      {isToday && (
                                        <div className="mt-2">
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-yellow-300 text-yellow-900 animate-pulse">
                                            TODAY
                                          </span>
                                        </div>
                                      )}
                                      <div className="mt-2 text-white text-xs font-semibold">
                                        {tasks.length} {tasks.length === 1 ? 'Task' : 'Tasks'}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Tasks for this date */}
                                  <div className="p-2 sm:p-3 space-y-2 sm:space-y-3">
                                    {tasks.length === 0 ? (
                                      <div className="text-center py-8 text-gray-400">
                                        <CalendarDaysIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                        <p className="text-xs">No tasks</p>
                                      </div>
                                    ) : (() => {
                                      const MAX_TASKS_TO_SHOW = 3;
                                      const isExpanded = expandedDates.has(dateKey);
                                      const tasksToShow = isExpanded ? tasks : tasks.slice(0, MAX_TASKS_TO_SHOW);
                                      const remainingCount = tasks.length - MAX_TASKS_TO_SHOW;
                                      
                                      return (
                                        <>
                                          {tasksToShow.map((task, idx) => (
                                            <div 
                                              key={task.id || idx} 
                                              className="bg-white border-2 border-gray-200 rounded-xl p-3 sm:p-4 hover:border-teal-400 hover:shadow-professional transition-all duration-300 card-hover"
                                            >
                                              {/* Task Name */}
                                              <h4 className="text-xs sm:text-sm font-bold text-gray-900 mb-2 line-clamp-2 min-h-[2rem] sm:min-h-[2.5rem]">
                                                {task.name}
                                              </h4>
                                              
                                              {/* Date Range Badge */}
                                              <div className="mb-2 text-xs">
                                                <div className="inline-flex items-center px-2 py-1 bg-blue-50 rounded border border-blue-200">
                                                  <svg className="w-3 h-3 mr-1 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                                  </svg>
                                                  <span className="text-blue-900 font-medium">
                                                    {task.startDate.format('MMM D')} - {task.endDate.format('MMM D')}
                                                  </span>
                                                </div>
                                              </div>
                                              
                                              {/* Start/End Indicators */}
                                              {(task.isStart || task.isEnd) && (
                                                <div className="mb-2 flex gap-1">
                                                  {task.isStart && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-green-100 text-green-800">
                                                      ▶ Start
                                                    </span>
                                                  )}
                                                  {task.isEnd && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-800">
                                                      ◀ Due
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                              
                                              {/* Status and Priority */}
                                              <div className="flex items-center gap-1 mb-2 flex-wrap">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                                  task.status?.toLowerCase().includes('progress') 
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : task.status?.toLowerCase().includes('complete')
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                  {task.status}
                                                </span>
                                                {task.priority && task.priority !== 'None' && (
                                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                                    task.priority?.toLowerCase() === 'urgent' 
                                                      ? 'bg-red-100 text-red-800'
                                                      : task.priority?.toLowerCase() === 'high'
                                                      ? 'bg-orange-100 text-orange-800'
                                                      : 'bg-blue-100 text-blue-800'
                                                  }`}>
                                                    {task.priority}
                                                  </span>
                                                )}
                                              </div>
                                              
                                              {/* Time Estimate */}
                                              {task.time_estimate && (
                                                <div className="mb-2 flex items-center text-xs text-gray-600">
                                                  <ClockIcon className="w-3 h-3 mr-1" />
                                                  <span className="font-medium">
                                                    {(task.time_estimate / 1000 / 60 / 60).toFixed(1)}h
                                                  </span>
                                                </div>
                                              )}
                                              
                                              {/* Actions */}
                                              {task.url && (
                                                <div className="pt-2 border-t border-gray-200">
                                                  <a
                                                    href={task.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block w-full px-3 py-2 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-center"
                                                  >
                                                    Open in ClickUp
                                                  </a>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                          
                                          {/* View More Button */}
                                          {!isExpanded && remainingCount > 0 && (
                                            <button
                                              onClick={() => setExpandedDates(prev => new Set([...prev, dateKey]))}
                                              className="w-full px-3 py-2 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors border-2 border-teal-200 hover:border-teal-300 flex items-center justify-center gap-1"
                                            >
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                              </svg>
                                              View {remainingCount} more {remainingCount === 1 ? 'task' : 'tasks'}
                                            </button>
                                          )}
                                          
                                          {/* View Less Button */}
                                          {isExpanded && tasks.length > MAX_TASKS_TO_SHOW && (
                                            <button
                                              onClick={() => setExpandedDates(prev => {
                                                const next = new Set(prev);
                                                next.delete(dateKey);
                                                return next;
                                              })}
                                              className="w-full px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border-2 border-gray-200 hover:border-gray-300 flex items-center justify-center gap-1"
                                            >
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                              </svg>
                                              Show less
                                            </button>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              );
                            })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* Scroll Indicator */}
                    <div className="text-center mt-2">
                      <p className="text-xs text-gray-500 flex items-center justify-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                        </svg>
                        Scroll left/right to view more dates
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </p>
                    </div>
                    
                    {/* If no tasks with dates */}
                    {workloadData.tasks.filter(t => t.start_date && t.due_date).length === 0 && (
                      <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 mt-4">
                        <CalendarDaysIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 font-medium">No tasks with scheduled dates</p>
                        <p className="text-gray-500 text-sm mt-2">Tasks without start/end dates won't appear in the timeline</p>
                      </div>
                    )}
                    </div>
                  </div>

                  {/* Unscheduled Tasks Section - Outside overflow container */}
                {workloadData.tasks && workloadData.tasks.some(t => !t.start_date || !t.due_date) && (
                  <div className="mt-6 border-t-2 border-gray-300 pt-6 w-full">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-bold text-gray-900 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Unscheduled Tasks
                      </h4>
                      <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-bold ${
                        workloadData.tasks.filter(t => !t.start_date || !t.due_date).length > 3
                          ? 'bg-red-100 text-red-700'
                          : workloadData.tasks.filter(t => !t.start_date || !t.due_date).length > 1
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {workloadData.tasks.filter(t => !t.start_date || !t.due_date).length}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">Tasks without start or due dates. Click to schedule them.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 w-full">
                      {workloadData.tasks
                        .filter(t => !t.start_date || !t.due_date)
                        .map((task, idx) => (
                          <div
                            key={task.id || idx}
                            className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-teal-400 hover:shadow-lg transition-all duration-300"
                          >
                            <h5 className="text-sm font-bold text-gray-900 mb-2 line-clamp-2 min-h-[2.5rem]">
                              {task.name}
                            </h5>
                            
                            {/* Status and Priority */}
                            <div className="flex items-center gap-1 mb-3 flex-wrap">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                task.status?.toLowerCase().includes('progress') 
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : task.status?.toLowerCase().includes('complete')
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {task.status || 'N/A'}
                              </span>
                              {task.priority && task.priority !== 'None' && task.priority !== 'N/A' && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                  task.priority?.toLowerCase() === 'urgent' 
                                    ? 'bg-red-100 text-red-800'
                                    : task.priority?.toLowerCase() === 'high'
                                    ? 'bg-orange-100 text-orange-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {task.priority}
                                </span>
                              )}
                            </div>

                            {/* List/Project */}
                            {task.list && task.list !== 'N/A' && (
                              <div className="mb-3 text-xs text-gray-600">
                                <span className="font-medium">List:</span> {task.list}
                              </div>
                            )}

                            {/* Missing Dates Info */}
                            <div className="mb-3 text-xs text-orange-600">
                              {!task.start_date && !task.due_date && (
                                <span className="flex items-center">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Missing start and due dates
                                </span>
                              )}
                              {!task.start_date && task.due_date && (
                                <span className="flex items-center">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Missing start date
                                </span>
                              )}
                              {task.start_date && !task.due_date && (
                                <span className="flex items-center">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Missing due date
                                </span>
                              )}
                            </div>

                            {/* Actions */}
                            <div className="pt-3 border-t border-gray-200">
                              <button
                                onClick={() => handleEditTask(task)}
                                className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-all duration-200 border-2 border-teal-200 hover:border-teal-300"
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit Dates
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                </>
              )}
            </>
          )}
        </div>
        )}


        {/* My Performance Metrics */}
        {showPerformanceDetail && performanceData && performanceData.summary && (
          <div className="mb-4 sm:mb-6 bg-white shadow-professional rounded-2xl p-6 border border-gray-200">
            {/* Header with Period Buttons */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
              <h3 className="text-xl font-bold text-gray-900">My Performance Metrics</h3>
              <div className="flex gap-2">
                {[7, 14, 30].map((days) => (
                  <button
                    key={days}
                    onClick={() => setPerformanceDays(days)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      performanceDays === days
                        ? 'bg-green-500 text-white shadow-professional'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>

            {performanceLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-12 w-12 border-4 border-purple-200 border-t-purple-600 rounded-full"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Performance Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Tasks Completed */}
                  <div className="bg-green-50 rounded-xl p-5 border border-green-200">
                    <div className="flex items-center justify-between mb-3">
                      <CheckCircleIcon className="h-6 w-6 text-green-600" />
                      <div className="flex items-center text-green-600 text-xs font-semibold">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        +8%
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{performanceData.summary.totalCompleted}</div>
                    <div className="text-sm text-gray-600">Tasks Completed</div>
                  </div>

                  {/* New Tasks */}
                  <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <PlusCircleIcon className="h-6 w-6 text-blue-600" />
                      <div className="flex items-center text-green-600 text-xs font-semibold">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        +12%
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{performanceData.summary.totalNewTasks}</div>
                    <div className="text-sm text-gray-600">New Tasks</div>
                  </div>

                  {/* Avg Daily Tasks */}
                  <div className="bg-pink-50 rounded-xl p-5 border border-pink-200">
                    <div className="flex items-center justify-between mb-3">
                      <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <div className="flex items-center text-green-600 text-xs font-semibold">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        +5%
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{performanceData.summary.avgDailyTasks}</div>
                    <div className="text-sm text-gray-600">Avg Daily Tasks</div>
                  </div>

                  {/* Net Change */}
                  <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                    <div className="flex items-center justify-between mb-3">
                      <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      <div className="flex items-center text-red-600 text-xs font-semibold">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        Backlog
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">
                      {performanceData.summary.totalNewTasks - performanceData.summary.totalCompleted > 0 ? '+' : ''}
                      {performanceData.summary.totalNewTasks - performanceData.summary.totalCompleted}
                    </div>
                    <div className="text-sm text-gray-600">Net Change</div>
                  </div>
                </div>

                {/* Daily Activity Log */}
                {performanceData.snapshots && performanceData.snapshots.length > 0 && (
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                    <h4 className="text-base font-bold text-gray-900 mb-4">Daily Activity Log</h4>
                    <div className="space-y-3">
                      {performanceData.snapshots.slice(0, 7).map((snap, idx) => {
                        const maxTasks = Math.max(...performanceData.snapshots.slice(0, 7).map(s => s.totalTasks || 0), 1);
                        const barWidth = ((snap.totalTasks || 0) / maxTasks) * 100;
                        
                        return (
                          <div key={idx} className="flex items-center gap-4">
                            <div className="text-sm font-medium text-gray-700 w-28">
                              {moment(snap.date).format('ddd, MMM D')}
                            </div>
                            <div className="flex-1 flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-6 overflow-hidden">
                                <div 
                                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full transition-all duration-500"
                                  style={{ width: `${barWidth}%` }}
                                ></div>
                              </div>
                              <div className="text-sm font-semibold text-gray-900 w-8 text-right">
                                {snap.totalTasks || 0}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* My ClickUp Tasks Section */}
        {showTaskDetail && showTasksSection && myTasks.length > 0 && (
          <div className="mb-6 bg-white shadow-lg rounded-xl p-6 border-2 border-indigo-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <ChartBarIcon className="h-6 w-6 mr-2 text-indigo-600" />
                My ClickUp Tasks
              </h3>
              <button
                onClick={() => setShowTasksSection(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div className="bg-indigo-50 rounded-lg p-4 border-2 border-indigo-200">
                <div className="text-sm font-medium text-indigo-900">Total Tasks</div>
                <div className="text-2xl font-bold text-indigo-700 mt-1">{myTasks.length}</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                <div className="text-sm font-medium text-blue-900">Unique Lists</div>
                <div className="text-2xl font-bold text-blue-700 mt-1">
                  {new Set(myTasks.map(t => t.list)).size}
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
                <div className="text-sm font-medium text-purple-900">Statuses</div>
                <div className="text-2xl font-bold text-purple-700 mt-1">
                  {new Set(myTasks.map(t => t.status)).size}
                </div>
              </div>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {myTasks.map((task, idx) => (
                <div 
                  key={task.id || idx}
                  className="border-2 border-gray-200 rounded-lg p-4 hover:border-indigo-400 hover:shadow-md transition-all duration-200 bg-gradient-to-br from-white to-gray-50"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">{task.name}</h4>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          📋 {task.list}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                          task.status?.toLowerCase().includes('progress') 
                            ? 'bg-yellow-100 text-yellow-800'
                            : task.status?.toLowerCase().includes('complete')
                            ? 'bg-green-100 text-green-800'
                            : task.status?.toLowerCase().includes('test')
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          ⚡ {task.status}
                        </span>
                        {task.priority && task.priority !== 'N/A' && task.priority !== 'None' && (
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                            task.priority?.toLowerCase() === 'urgent' 
                              ? 'bg-red-100 text-red-800'
                              : task.priority?.toLowerCase() === 'high'
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            🎯 {task.priority}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                    {task.space && task.space !== 'N/A' && (
                      <div>
                        <span className="font-medium">Space:</span> {task.space}
                      </div>
                    )}
                    {task.folder && task.folder !== 'N/A' && (
                      <div>
                        <span className="font-medium">Folder:</span> {task.folder}
                      </div>
                    )}
                    {task.dueDate && (
                      <div className="flex items-center">
                        <span className="font-medium">Due:</span>
                        <span className="ml-1">{moment(parseInt(task.dueDate)).format('MMM DD, YYYY')}</span>
                      </div>
                    )}
                    {task.assignees && task.assignees.length > 0 && (
                      <div>
                        <span className="font-medium">Assignees:</span> {task.assignees.join(', ')}
                      </div>
                    )}
                  </div>

                  {task.url && (
                    <a
                      href={task.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      View in ClickUp
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Period Selector */}
        {showAttendanceDetail && (
        <div className="mb-6 animate-fade-in-up">
          <div className="flex space-x-1 bg-white rounded-xl p-1.5 shadow-professional border border-gray-100">
            {[
              { key: 'week', label: 'This Week' },
              { key: 'month', label: 'This Month' },
              { key: 'year', label: 'This Year' },
              { key: 'custom', label: 'Custom Range' }
            ].map((period) => (
              <button
                key={period.key}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedPeriod(period.key);
                }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  selectedPeriod === period.key
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {period.label}
              </button>
            ))}
          </div>

          {/* Custom Date Range Picker */}
          {selectedPeriod === 'custom' && (
            <div className="mt-4 p-5 bg-white rounded-xl shadow-professional border border-gray-100 animate-fade-in-up relative z-10">
              {/* Custom CSS to increase z-index of date picker popup */}
              <style>{`
                .react-datepicker-popper {
                  z-index: 99999 !important;
                  position: fixed !important;
                }
                .react-datepicker {
                  z-index: 99999 !important;
                  position: relative !important;
                }
                .react-datepicker__portal {
                  z-index: 99999 !important;
                }
                .react-datepicker__triangle {
                  z-index: 99999 !important;
                }
                div[class*="react-datepicker-popper"] {
                  z-index: 99999 !important;
                }
              `}</style>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Date Range</label>
              <div className="flex items-center space-x-3">
                <div className="flex-1 relative z-50">
                  <DatePicker
                    selected={customStartDate}
                    onChange={(date) => setCustomStartDate(date)}
                    selectsStart
                    startDate={customStartDate}
                    endDate={customEndDate}
                    maxDate={customEndDate}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm px-3 py-2"
                    placeholderText="Start date"
                    dateFormat="MMM dd, yyyy"
                    portalId="root-portal"
                    popperProps={{
                      strategy: 'fixed',
                    }}
                    popperModifiers={[
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 8],
                        },
                      },
                      {
                        name: 'preventOverflow',
                        options: {
                          rootBoundary: 'viewport',
                          tether: false,
                          altAxis: true,
                        },
                      },
                    ]}
                    popperClassName="react-datepicker-popper-custom"
                  />
                </div>
                <span className="text-gray-500">to</span>
                <div className="flex-1 relative z-50">
                  <DatePicker
                    selected={customEndDate}
                    onChange={(date) => setCustomEndDate(date)}
                    selectsEnd
                    startDate={customStartDate}
                    endDate={customEndDate}
                    minDate={customStartDate}
                    maxDate={new Date()}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm px-3 py-2"
                    placeholderText="End date"
                    dateFormat="MMM dd, yyyy"
                    portalId="root-portal"
                    popperProps={{
                      strategy: 'fixed',
                    }}
                    popperModifiers={[
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 8],
                        },
                      },
                      {
                        name: 'preventOverflow',
                        options: {
                          rootBoundary: 'viewport',
                          tether: false,
                          altAxis: true,
                        },
                      },
                    ]}
                    popperClassName="react-datepicker-popper-custom"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Content Section with Inline Loader */}
        {showAttendanceDetail && (
        <div className="relative min-h-[400px]">
          {/* Inline Loader Overlay */}
          {updating && (
            <div className="absolute inset-0 bg-white/95 backdrop-blur-md z-50 flex items-center justify-center rounded-2xl min-h-[400px] shadow-professional">
              <div className="flex flex-col items-center animate-fade-in">
                <div className="spinner mb-4"></div>
                <p className="text-sm font-semibold text-gray-700">Updating data...</p>
              </div>
            </div>
          )}

          {/* Summary Stats - Professional Design with Animations */}
          <div className={`grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-8 transition-opacity duration-300 ${updating ? 'opacity-40' : 'opacity-100'}`} style={{ position: 'relative', zIndex: 1 }}>
          {/* Working Days Card */}
          <div className="group relative bg-white rounded-2xl shadow-professional hover:shadow-professional-lg overflow-hidden border border-blue-100 card-hover animate-fade-in-up stagger-1">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-professional group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <CalendarDaysIcon className="h-7 w-7 text-white" />
                </div>
              </div>
              <dt className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Working Days</dt>
              <dd className="text-4xl font-bold text-gray-900 mb-1 transition-all duration-300 group-hover:text-blue-600">
                <span className="inline-block animate-numberCount">{attendanceData?.summary?.workingDays || 0}</span>
              </dd>
              <p className="mt-2 text-xs font-medium text-gray-500">
                of {attendanceData?.summary?.totalDays || 0} days
              </p>
            </div>
          </div>

          {/* Total Hours Card */}
          <div className="group relative bg-white rounded-2xl shadow-professional hover:shadow-professional-lg overflow-hidden border border-green-100 card-hover animate-fade-in-up stagger-2">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-professional group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <ClockIcon className="h-7 w-7 text-white" />
                </div>
              </div>
              <dt className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Total Hours</dt>
              <dd className="text-4xl font-bold text-gray-900 mb-1 transition-all duration-300 group-hover:text-green-600">
                <span className="inline-block animate-numberCount">{attendanceData?.summary?.totalHours || 0}</span><span className="text-2xl ml-1">h</span>
              </dd>
              <p className="mt-2 text-xs font-medium text-gray-500">
                {formatPeriodLabel()}
              </p>
            </div>
          </div>

          {/* Average Per Day Card */}
          <div className="group relative bg-white rounded-2xl shadow-professional hover:shadow-professional-lg overflow-hidden border border-purple-100 card-hover animate-fade-in-up stagger-3">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-professional group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <ChartBarIcon className="h-7 w-7 text-white" />
                </div>
              </div>
              <dt className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Avg/Day</dt>
              <dd className="text-4xl font-bold text-gray-900 mb-1 transition-all duration-300 group-hover:text-purple-600">
                <span className="inline-block animate-numberCount">{attendanceData?.summary?.avgHoursPerDay || 0}</span><span className="text-2xl ml-1">h</span>
              </dd>
              <p className="mt-2 text-xs font-medium text-gray-500">
                Average hours
              </p>
            </div>
          </div>

          {/* Today's Hours Card */}
          <div className="group relative bg-white rounded-2xl shadow-professional hover:shadow-professional-lg overflow-hidden border border-orange-100 card-hover animate-fade-in-up stagger-4">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl shadow-professional group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <ClockIcon className="h-7 w-7 text-white" />
                </div>
              </div>
              <dt className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Today's Hours</dt>
              <dd className="text-4xl font-bold text-gray-900 mb-1 transition-all duration-300 group-hover:text-orange-600">
                <span className="inline-block animate-numberCount">{attendanceData?.summary?.todayHours || 0}</span><span className="text-2xl ml-1">h</span>
              </dd>
              <p className="mt-2 text-xs font-medium text-gray-500">
                {moment().format('MMM DD, YYYY')}
              </p>
            </div>
          </div>

          {/* My ClickUp Tasks Card */}
          <div 
            className="group relative bg-white rounded-2xl shadow-professional hover:shadow-professional-lg overflow-hidden border border-indigo-100 card-hover cursor-pointer animate-fade-in-up stagger-5"
            onClick={() => myTasks.length > 0 && setShowTasksSection(!showTasksSection)}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-professional group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <ChartBarIcon className="h-7 w-7 text-white" />
                </div>
              </div>
              <dt className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">My Tasks</dt>
              <dd className="text-4xl font-bold text-gray-900 mb-1 transition-all duration-300 group-hover:text-indigo-600">
                {clickupLoading ? (
                  <div className="animate-spin h-10 w-10 border-3 border-indigo-300 border-t-indigo-600 rounded-full"></div>
                ) : (
                  <span className="inline-block animate-numberCount">{myTasks.length}</span>
                )}
              </dd>
              <p className="mt-2 text-xs font-medium text-gray-500">
                {myTasks.length > 0 ? 'Click to view tasks' : 'ClickUp tasks'}
              </p>
            </div>
          </div>

	        </div>
        )}

        {/* Performance Analysis */}
        {attendanceData?.workingPolicy && (
          <div className={`bg-white shadow-professional-lg rounded-2xl mb-8 border border-gray-200 transition-all duration-300 ${updating ? 'opacity-40' : 'opacity-100'} animate-fade-in-up`}>
            <div className="p-7">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <svg className="w-6 h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Performance Analysis
              </h3>
              
              {(() => {
                // Declare workDays first to avoid TDZ issues
                const workDays = attendanceData?.workingPolicy?.workDays || null;
                
                let requiredHours;
                let startDate, endDate;
                let workingDaysInfo = { workingDays: 0, holidayCount: 0 };
                const employmentType = user?.employementType || attendanceData?.user?.employmentType || 'Full-Time';
                
                // Get leave figures from summary (already calculated in backend).
                // leaveDays is fractional (Half Day = 0.5); leaveHoursDeducted is the
                // exact number of hours those leaves exempt from the expected total.
                const leaveDays = attendanceData.summary?.leaveDays || 0;
                const leaveHoursDeducted = Number(attendanceData.summary?.leaveHoursDeducted || 0);
                
                // Determine date range based on selected period
                if (selectedPeriod === 'week') {
                  startDate = moment().startOf('isoWeek'); // Monday
                  endDate = moment().endOf('isoWeek'); // Sunday
                  workingDaysInfo = calculateWorkingDaysExcludingHolidays(startDate.toDate(), endDate.toDate(), employmentType, workDays);
                  // Subtract leave days from working days for required hours calculation
                  requiredHours = Math.max(0, attendanceData.workingPolicy.requiredDailyHours * workingDaysInfo.workingDays - leaveHoursDeducted);
                } else if (selectedPeriod === 'month') {
                  startDate = moment().startOf('month');
                  endDate = moment().endOf('month');
                  workingDaysInfo = calculateWorkingDaysExcludingHolidays(startDate.toDate(), endDate.toDate(), employmentType, workDays);
                  requiredHours = Math.max(0, attendanceData.workingPolicy.requiredDailyHours * workingDaysInfo.workingDays - leaveHoursDeducted);
                } else if (selectedPeriod === 'year') {
                  startDate = moment().startOf('year');
                  endDate = moment().endOf('year');
                  workingDaysInfo = calculateWorkingDaysExcludingHolidays(startDate.toDate(), endDate.toDate(), employmentType, workDays);
                  requiredHours = Math.max(0, attendanceData.workingPolicy.requiredDailyHours * workingDaysInfo.workingDays - leaveHoursDeducted);
                } else if (selectedPeriod === 'custom') {
                  startDate = moment(customStartDate);
                  endDate = moment(customEndDate);
                  workingDaysInfo = calculateWorkingDaysExcludingHolidays(startDate.toDate(), endDate.toDate(), employmentType, workDays);
                  requiredHours = Math.max(0, attendanceData.workingPolicy.requiredDailyHours * workingDaysInfo.workingDays - leaveHoursDeducted);
                } else {
                  startDate = moment().startOf('month');
                  endDate = moment().endOf('month');
                  workingDaysInfo = calculateWorkingDaysExcludingHolidays(startDate.toDate(), endDate.toDate(), employmentType, workDays);
                  requiredHours = Math.max(0, attendanceData.workingPolicy.requiredDailyHours * workingDaysInfo.workingDays - leaveHoursDeducted);
                }
                
                // Use adjusted total hours (already excludes leave days) from summary
                const totalHoursWorked = attendanceData.summary?.totalHours || 0;
                const completion = getCompletionStatus(totalHoursWorked, requiredHours);
                const hoursNeeded = Math.max(0, requiredHours - totalHoursWorked);
                const message = completion.percentage >= 100 
                  ? "Excellent Work! You've reached your target!" 
                  : completion.percentage >= 75 
                  ? "Great Work!" 
                  : completion.percentage >= 50 
                  ? "Keep Going!" 
                  : "You're making progress!";
                
                return (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left: Progress Circle */}
                    <div className="flex flex-col items-center">
                      <div className="relative w-64 h-64 mb-4">
                        <svg className="transform -rotate-90 w-64 h-64">
                          {/* Background circle - light purple */}
                          <circle
                            cx="128"
                            cy="128"
                            r="110"
                            stroke="currentColor"
                            strokeWidth="16"
                            fill="none"
                            className="text-purple-200"
                          />
                          {/* Progress circle - blue */}
                          <circle
                            cx="128"
                            cy="128"
                            r="110"
                            stroke="currentColor"
                            strokeWidth="16"
                            fill="none"
                            strokeDasharray={`${2 * Math.PI * 110}`}
                            strokeDashoffset={`${2 * Math.PI * 110 * (1 - Math.min(completion.percentage, 100) / 100)}`}
                            className="progress-animate text-blue-500"
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            {/* Target Icon */}
                            <div className="mb-2 flex justify-center">
                              <div className="relative">
                                {/* Target circle */}
                                <div className="w-16 h-16 rounded-full border-4 border-red-500 bg-white flex items-center justify-center">
                                  <div className="w-8 h-8 rounded-full border-2 border-red-500 bg-white flex items-center justify-center">
                                    <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="text-5xl font-bold text-blue-600 mb-1">
                              {completion.percentage.toFixed(0)}%
                            </div>
                            <div className="text-sm text-gray-600">Complete</div>
                          </div>
                        </div>
                      </div>
                      <div className="text-xl font-bold text-blue-600">{message}</div>
                    </div>

                    {/* Right: Info Cards */}
                    <div className="space-y-4">
                      {/* Hours Completed Card */}
                      <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-professional">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <ClockIcon className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Hours Completed</div>
                            <div className="text-2xl font-bold text-gray-900">{(attendanceData.summary?.totalHours || 0).toFixed(1)} hrs</div>
                            {attendanceData.summary?.fullDayLeaveDays > 0 && (
                              <div className="text-xs text-gray-500 mt-1">
                                (Excluding {attendanceData.summary.fullDayLeaveDays} full-day leave{attendanceData.summary.fullDayLeaveDays !== 1 ? 's' : ''})
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Required Hours Card */}
                      <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-professional">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Required Hours</div>
                            <div className="text-2xl font-bold text-gray-900">{requiredHours} hrs</div>
                          </div>
                        </div>
                      </div>

                      {/* Working Policy Card */}
                      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-5 text-white shadow-professional">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-sm text-white/90">Working Policy</div>
                            <div className="text-xl font-bold text-white">{attendanceData.workingPolicy.policyName}</div>
                            <div className="text-sm text-white/80 mt-1">
                              {attendanceData.workingPolicy.requiredDailyHours} hours/day • {formatDayCount(Math.max(0, workingDaysInfo.workingDays - leaveDays))} working days
                              {(workingDaysInfo.holidayCount > 0 || leaveDays > 0) && (
                                <span className="block mt-1 text-xs text-white/70">
                                  {workingDaysInfo.holidayCount > 0 && `${workingDaysInfo.holidayCount} holiday${workingDaysInfo.holidayCount > 1 ? 's' : ''} `}
                                  {leaveDays > 0 && `${formatDayCount(leaveDays)} leave day${leaveDays !== 1 ? 's' : ''} `}
                                  excluded
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Information Card */}
                      {hoursNeeded > 0 && (
                        <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-200 shadow-professional">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                            </div>
                            <div className="text-sm text-gray-700">
                              You need {hoursNeeded.toFixed(1)} more hours to reach your target!
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
        </div>
        )}

        {/* Recent Activity and Daily Breakdown */}
        {/* Upcoming Holidays */}
        {showHolidayDetail && upcomingHolidays && upcomingHolidays.length > 0 && (
          <div className={`bg-white shadow-professional-lg rounded-2xl mb-8 border border-gray-200 transition-all duration-300 animate-fade-in-up`}>
            <div className="p-5">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <CalendarDaysIcon className="w-6 h-6 text-blue-600 mr-2" />
                Upcoming Holidays
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {upcomingHolidays.map((holiday) => {
                  const holidayDate = moment(holiday.holidayDate);
                  const daysUntil = holidayDate.diff(moment(), 'days');
                  const isToday = daysUntil === 0;
                  const isSoon = daysUntil > 0 && daysUntil <= 7;
                  const isUpcoming = daysUntil > 7;
                  
                  return (
                    <div
                      key={holiday.id}
                      className={`rounded-lg p-3 border-2 transition-all duration-300 hover:shadow-md ${
                        isToday
                          ? 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-300'
                          : isSoon
                          ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-300'
                          : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                          isToday
                            ? 'bg-orange-500'
                            : isSoon
                            ? 'bg-yellow-500'
                            : 'bg-blue-500'
                        }`}>
                          <CalendarDaysIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-gray-900 mb-0.5 text-base">
                            {holiday.holidayName}
                          </h4>
                          <div className="mb-1">
                            {isToday && (
                              <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800 mr-1.5">
                                Today!
                              </span>
                            )}
                            {isSoon && !isToday && (
                              <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 mr-1.5">
                                In {daysUntil} day{daysUntil > 1 ? 's' : ''}
                              </span>
                            )}
                            {isUpcoming && (
                              <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                {daysUntil} days away
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-700 mb-1.5">
                            {holidayDate.format('dddd, MMMM D, YYYY')}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            {holiday.applicableTo !== 'Both' && (
                              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                holiday.applicableTo === 'Full-Time'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-purple-100 text-purple-800'
                              }`}>
                                {holiday.applicableTo}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {showAttendanceDetail && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in-up">
          {/* Recent Attendance Logs */}
          <div className="bg-white shadow-professional rounded-2xl border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Recent Attendance Logs
            </h3>
            {recentAttendanceLogs.length > 0 ? (
              <div className="space-y-3">
                {recentAttendanceLogs.map((log, index) => {
                  const isCheckIn = log.punchLogType === 'CheckIn' || log.punchLogType === 'Check In';
                  const logTimeIst = moment(log.logTime).tz('Asia/Kolkata');
                  return (
                    <div 
                      key={`${log.logTime}-${index}`} 
                      className={`rounded-lg p-4 ${
                        isCheckIn 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-blue-50 border border-blue-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-8 h-8 rounded flex items-center justify-center ${
                          isCheckIn ? 'bg-green-500' : 'bg-blue-500'
                        }`}>
                          {isCheckIn ? (
                            <ArrowRightIcon className="w-5 h-5 text-white" />
                          ) : (
                            <ArrowLeftIcon className="w-5 h-5 text-white" />
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 mb-1">
                            {isCheckIn ? 'Checked In' : 'Checked Out'}
                          </div>
                          <div className="text-sm text-gray-600 mb-1">
                            Office - Main Building
                          </div>
                          <div className="text-sm text-gray-700 font-medium">
                            {logTimeIst.format('h:mm A')} • {logTimeIst.format('ddd, MMM D')}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            )}
          </div>

          {/* Daily Hours Breakdown */}
          <div className="bg-white shadow-professional rounded-2xl border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center">
              <ClockIcon className="w-5 h-5 text-purple-600 mr-2" />
              Daily Hours Breakdown
            </h3>
            {attendanceData?.dailyHours?.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {attendanceData.dailyHours.slice(0, 10).map((day) => {
                  const isComplete = attendanceData.workingPolicy 
                    ? day.hoursWorked >= attendanceData.workingPolicy.requiredDailyHours 
                    : true;
                  const requiredHours = attendanceData.workingPolicy?.requiredDailyHours || 8;
                  const progressPercentage = Math.min((day.hoursWorked / requiredHours) * 100, 100);
                  
                  return (
                    <div key={day.date} className="bg-white rounded-lg border border-gray-200 p-4">
                      {/* Date Header */}
                      <div className="mb-3">
                        <div className="font-semibold text-gray-900">
                          {moment(day.date).format('dddd')}
                        </div>
                        <div className="text-sm text-gray-600">
                          {moment(day.date).format('MMM D, YYYY')}
                        </div>
                      </div>

                      {/* In/Out Times */}
                      {day.firstCheckIn && (
                        <div className="flex items-center gap-4 mb-3 text-sm">
                          <div className="text-gray-700">
                            <span className="font-medium">In:</span> {moment(day.firstCheckIn).tz('Asia/Kolkata').format('h:mm A')}
                          </div>
                          {day.lastCheckOut && (
                            <div className="text-gray-700">
                              <span className="font-medium">Out:</span> {moment(day.lastCheckOut).tz('Asia/Kolkata').format('h:mm A')}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Hours and Status */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-lg font-bold text-gray-900">
                          {day.hoursWorked ? day.hoursWorked.toFixed(1) : 0}h
                        </div>
                        {isComplete ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-500" />
                        ) : (
                          <XMarkIcon className="w-5 h-5 text-red-500" />
                        )}
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            isComplete ? 'bg-green-500' : 'bg-orange-500'
                          }`}
                          style={{ width: `${progressPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No daily hours data</p>
            )}
          </div>
        </div>
        )}
      </div>

      {checkoutRedirectWarning && (
        <div className="fixed inset-0 z-[10000] grid place-items-center p-4 sm:p-6 overflow-y-auto animate-fade-in">
          <div
            className="fixed inset-0 bg-gray-900/70 backdrop-blur-md transition-opacity animate-fade-in"
            onClick={() => setCheckoutRedirectWarning(null)}
          ></div>

          <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl text-left overflow-hidden shadow-professional-lg transform transition-all duration-300 animate-scale-in">
            <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-orange-600 px-6 py-5 shadow-professional">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-white/20 backdrop-blur-md border-2 border-white/30">
                  <ExclamationTriangleIcon className="h-7 w-7 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-white">Use Checkout Request</h3>
                  <p className="text-sm text-orange-100 mt-0.5">
                    This date already has a check-in record
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white px-6 py-6 space-y-4">
              <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3">
                <div className="text-sm font-semibold text-orange-900">
                  {checkoutRedirectWarning?.date
                    ? moment(checkoutRedirectWarning.date).format('MMMM DD, YYYY')
                    : 'Selected date'}
                </div>
                {checkoutRedirectWarning?.firstCheckIn && (
                  <div className="mt-1 text-sm text-orange-700">
                    Check-in recorded at {moment(checkoutRedirectWarning.firstCheckIn).format('h:mm A')}
                  </div>
                )}
              </div>

              <p className="text-sm text-gray-700">
                Attendance correction is only for dates with no attendance record. Since you already checked in on this date, submit a checkout request instead.
              </p>
            </div>

            <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse gap-3 border-t border-gray-200">
              <button
                type="button"
                onClick={handleContinueToCheckoutRequest}
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-xl border border-transparent shadow-professional px-6 py-3 bg-gradient-to-r from-orange-600 to-amber-600 text-base font-semibold text-white hover:from-orange-700 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-300"
              >
                Continue to Checkout Request
              </button>
              <button
                type="button"
                onClick={() => setCheckoutRedirectWarning(null)}
                className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-xl border-2 border-gray-300 shadow-professional px-6 py-3 bg-white text-base font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Request Modal */}
      {showRequestModal && (
        <EmployeeDashboardDetailSheet
          isOpen={showRequestModal}
          title="Request Checkout Time"
          summary="Request checkout time for dates with missing checkout"
          eyebrow="Quick action"
          icon={<ClockIcon className="h-5 w-5 text-orange-600" />}
          iconClassName="bg-orange-50 text-orange-700 ring-orange-200"
          onClose={() => setShowRequestModal(false)}
          footer={(
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowRequestModal(false)}
                disabled={submitting}
                className="inline-flex w-full justify-center rounded-xl border border-slate-200 bg-white px-6 py-3 text-base font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitRequest}
                disabled={submitting || !requestDate || eligibleDates.length === 0 || requestReason.length >= 500}
                className="inline-flex w-full items-center justify-center rounded-xl border border-transparent bg-orange-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {submitting ? (
                  <>
                    <svg className="-ml-1 mr-3 h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </button>
            </div>
          )}
        >
                <div className="space-y-5">
                  {/* Date Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Date <span className="text-red-500">*</span>
                      <span className="ml-2 text-xs font-normal text-orange-600">
                        (Dates with missing checkouts are highlighted in orange)
                      </span>
                    </label>
                    
                    {/* Custom CSS for highlighting eligible dates */}
                    <style>{`
                      .react-datepicker__day--highlighted {
                        background-color: #fb923c !important;
                        color: white !important;
                        font-weight: bold !important;
                        border-radius: 0.5rem !important;
                        position: relative !important;
                        box-shadow: 0 0 0 2px #f97316 !important;
                      }
                      .react-datepicker__day--highlighted:hover {
                        background-color: #f97316 !important;
                        transform: scale(1.1);
                      }
                      .react-datepicker__day--highlighted::after {
                        content: '⚠️';
                        position: absolute;
                        top: -8px;
                        right: -8px;
                        font-size: 12px;
                      }
                      .react-datepicker__day--selected.react-datepicker__day--highlighted {
                        background-color: #ea580c !important;
                        box-shadow: 0 0 0 3px #ea580c !important;
                      }
                      .react-datepicker__day--keyboard-selected.react-datepicker__day--highlighted {
                        background-color: #f97316 !important;
                      }
                    `}</style>
                    
                    <DatePicker
                      selected={requestDate}
                      onChange={(date) => setRequestDate(date)}
                      filterDate={isDateEligible}
                      maxDate={new Date()}
                      minDate={moment().subtract(30, 'days').toDate()}
                      placeholderText="Select a date with missing checkout"
                      className="w-full border-2 border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-base px-4 py-3 transition-all duration-200"
                      dateFormat="MMM dd, yyyy"
                      highlightDates={eligibleDates}
                      inline={false}
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                    {eligibleDates.length === 0 ? (
                      <div className="mt-2 flex items-start">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                        <p className="text-sm text-red-600">
                          No eligible dates found. You can only request checkout for dates with missing checkout times.
                        </p>
                      </div>
                    ) : (
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                          <div className="flex-shrink-0">
                            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <p className="ml-2 text-sm text-orange-900 font-medium">
                            {eligibleDates.length} date(s) with missing checkout found
                          </p>
                        </div>
                        <p className="text-xs text-gray-600 flex items-start">
                          <span className="text-orange-500 mr-1">⚠️</span>
                          Orange highlighted dates in the calendar need checkout time
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Time Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Checkout Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={requestTime}
                      onChange={(e) => setRequestTime(e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-base px-4 py-3 transition-all duration-200"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">
                      Select the time you actually left the office
                    </p>
                  </div>

                  {/* Selected Date & Time Preview */}
                  {requestDate && requestTime && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-orange-900">
                          Requested Checkout:
                        </span>
                        <span className="text-base font-bold text-orange-700">
                          {moment(requestDate).format('MMM DD, YYYY')} at {requestTime}
                        </span>
                      </div>
                    </div>
                  )}

                  {checkoutLimitInfo && requestDate && (
                    <div className={`rounded-lg border px-4 py-3 ${
                      willExceedCheckoutLimit ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'
                    }`}>
                      <div className="flex items-start gap-2">
                        <ExclamationTriangleIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                          willExceedCheckoutLimit ? 'text-red-600' : 'text-orange-600'
                        }`} />
                        <div className="text-sm">
                          {checkoutLimitInfo.limit !== null ? (
                            <p className={willExceedCheckoutLimit ? 'text-red-700' : 'text-orange-800'}>
                              Monthly checkout limit: {formatLimitValue(checkoutLimitInfo.limit)} • Approved used: {formatLimitValue(checkoutLimitInfo.used || 0)} • This request adds 1.
                            </p>
                          ) : (
                            <p className="text-orange-800">Monthly checkout limit: Unlimited.</p>
                          )}
                          {willExceedCheckoutLimit && checkoutMonthSummary?.policy?.overLimitNotice && (
                            <p className="mt-2 text-xs text-red-700">{checkoutMonthSummary.policy.overLimitNotice}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Reason <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                    </label>
                    <textarea
                      value={requestReason}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Trim to 500 characters if copy-paste exceeds limit
                        if (value.length > 500) {
                          setRequestReason(value.substring(0, 500));
                        } else {
                          setRequestReason(value);
                        }
                      }}
                      maxLength={500}
                      rows={4}
                      className={`w-full border-2 rounded-lg shadow-sm focus:ring-2 text-base px-4 py-3 transition-all duration-200 resize-none ${
                        requestReason.length >= 500 
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                          : 'border-gray-300 focus:ring-orange-500 focus:border-orange-500'
                      }`}
                      placeholder="Why are you requesting this checkout time? (e.g., Forgot to punch out, System error, etc.)"
                    />
                    <p className={`mt-1.5 text-xs ${requestReason.length >= 500 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                      {requestReason.length}/500 characters
                    </p>
                  </div>

                  {/* Available Dates List - Enhanced with Quick Select */}
                  {eligibleDates.length > 0 && (
                    <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-orange-300 p-4 rounded-xl shadow-sm">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mt-1">
                          <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                            <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                          </div>
                        </div>
                        <div className="ml-4 flex-1">
                          <p className="text-sm font-bold text-orange-900 mb-3 flex items-center">
                            <span className="bg-orange-600 text-white text-xs font-bold px-2 py-1 rounded-full mr-2">
                              {eligibleDates.length}
                            </span>
                            Date(s) Missing Checkout - Click to Select:
                          </p>
                          <div className="grid grid-cols-1 gap-2">
                            {eligibleDates.slice(0, 7).map((date, idx) => {
                              const isSelected = requestDate && moment(date).format('YYYY-MM-DD') === moment(requestDate).format('YYYY-MM-DD');
                              return (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => setRequestDate(date)}
                                  className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all duration-200 text-left ${
                                    isSelected 
                                      ? 'bg-orange-600 border-orange-700 text-white shadow-md transform scale-105' 
                                      : 'bg-white border-orange-300 text-gray-900 hover:bg-orange-100 hover:border-orange-500 hover:shadow'
                                  }`}
                                >
                                  <div className="flex items-center">
                                    <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mr-3 font-bold ${
                                      isSelected ? 'bg-white text-orange-600' : 'bg-orange-100 text-orange-700'
                                    }`}>
                                      {moment(date).format('DD')}
                                    </span>
                                    <div>
                                      <div className={`font-semibold text-sm ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                                        {moment(date).format('MMM DD, YYYY')}
                                      </div>
                                      <div className={`text-xs ${isSelected ? 'text-orange-100' : 'text-gray-600'}`}>
                                        {moment(date).format('dddd')}
                                      </div>
                                    </div>
                                  </div>
                                  {isSelected && (
                                    <CheckCircleIcon className="h-6 w-6 text-white flex-shrink-0" />
                                  )}
                                  {!isSelected && (
                                    <svg className="h-5 w-5 text-orange-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  )}
                                </button>
                              );
                            })}
                            {eligibleDates.length > 7 && (
                              <p className="text-xs text-orange-700 font-medium mt-1 text-center">
                                + {eligibleDates.length - 7} more date(s) available in calendar
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Info Note */}
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-blue-800">Use This When</p>
                        <ul className="mt-2 text-sm text-blue-700 space-y-1">
                          <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>You already checked in but your check-out time is missing</span>
                          </li>
                        </ul>
                        <p className="mt-3 text-sm text-blue-700">
                          If the day has no attendance record at all, use attendance correction instead.
                        </p>
                        <p className="mt-3 text-sm text-blue-800 font-medium">
                          Your request will be sent to your reporting manager for approval.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
        </EmployeeDashboardDetailSheet>
      )}

      {/* Leave Request Modal */}
      {showLeaveModal && (
        <EmployeeDashboardDetailSheet
          isOpen={showLeaveModal}
          title="Apply for Leave"
          summary="Submit your leave request for manager approval"
          eyebrow="Quick action"
          icon={<CalendarDaysIcon className="h-5 w-5 text-violet-600" />}
          iconClassName="bg-violet-50 text-violet-700 ring-violet-200"
          onClose={() => setShowLeaveModal(false)}
          footer={(
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowLeaveModal(false)}
                disabled={submittingLeave}
                className="inline-flex w-full justify-center rounded-xl border border-slate-200 bg-white px-6 py-3 text-base font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitLeave}
                disabled={submittingLeave || !leaveReason.trim() || leaveReason.length >= 500}
                className="inline-flex w-full items-center justify-center rounded-xl border border-transparent bg-violet-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {submittingLeave ? (
                  <>
                    <svg className="-ml-1 mr-3 h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Leave Request'
                )}
              </button>
            </div>
          )}
        >
                <div className="space-y-5">
                  {/* Date Range Section */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Start Date
                      </label>
                      <DatePicker
                        selected={leaveStartDate}
                        onChange={(date) => {
                          setLeaveStartDate(date);
                          // If end date is before new start date, update end date
                          if (moment(leaveEndDate).isBefore(moment(date))) {
                            setLeaveEndDate(date);
                          }
                        }}
                        className="w-full border-2 border-gray-300 rounded-xl shadow-professional focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-base px-4 py-3 transition-all duration-300"
                        dateFormat="MMM dd, yyyy"
                        placeholderText="Select start date"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        End Date
                      </label>
                      <DatePicker
                        selected={leaveEndDate}
                        onChange={(date) => setLeaveEndDate(date)}
                        minDate={leaveStartDate}
                        className="w-full border-2 border-gray-300 rounded-xl shadow-professional focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-base px-4 py-3 transition-all duration-300"
                        dateFormat="MMM dd, yyyy"
                        placeholderText="Select end date"
                      />
                    </div>
                  </div>

                  {/* Duration Display */}
                  {leaveStartDate && leaveEndDate && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-purple-900">
                          Total Duration (Working Days):
                        </span>
                        <span className="text-lg font-bold text-purple-700">
                          {calculateWorkingDays(leaveStartDate, leaveEndDate)} day(s)
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Leave Type */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Leave Type
                    </label>
                    <select
                      value={leaveType}
                      onChange={(e) => setLeaveType(e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-base px-4 py-3 transition-all duration-200 bg-white"
                    >
                      <option value="Casual Leave">Casual Leave (Full Day)</option>
                      <option value="Sick Leave">Sick Leave (Full Day)</option>
                      <option value="Earned Leave">Earned Leave (Full Day)</option>
                      <option value="Emergency Leave">Emergency Leave (Full Day)</option>
                      <option value="Work from Home">Work from Home</option>
                      <option value="Short Leave">⏱️ Short Leave (1-2 hours - No hour deduction)</option>
                      <option value="Half Day">🕐 Half Day (4 hours credit)</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {(leaveMonthlySummary || (leaveLimitInfo && (leaveLimitInfo.isExempt || leaveLimitInfo.limit !== null))) && (
                    <div className={`rounded-lg border px-4 py-3 ${
                      willExceedLeaveLimit ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'
                    }`}>
                      <div className="flex items-start gap-2">
                        <ExclamationTriangleIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                          willExceedLeaveLimit ? 'text-red-600' : 'text-emerald-600'
                        }`} />
                        <div className="text-sm">
                          {leaveLimitInfo?.isExempt ? (
                            <p className="text-gray-700">This leave type is exempt from annual limits.</p>
                          ) : leaveLimitInfo?.limit !== null ? (
                            <p className={willExceedLeaveLimit ? 'text-red-700' : 'text-emerald-700'}>
                              Annual limit for <strong>{leaveType}</strong>: {formatLimitValue(leaveLimitInfo?.limit)} • Approved used: {formatLimitValue(leaveLimitInfo?.used || 0)} • This request adds {formatLimitValue(leaveRequestedDays)}.
                            </p>
                          ) : (
                            <p className="text-gray-700">No annual limit set for this leave type.</p>
                          )}
                          {leaveMonthlySummary && (
                            <p className="mt-1 text-xs text-gray-600">
                              This month approved used: {formatLimitValue(leaveMonthlyUsed)} day(s).
                            </p>
                          )}
                          {willExceedLeaveLimit && leaveMonthSummary?.policy?.overLimitNotice && (
                            <p className="mt-2 text-xs text-red-700">{leaveMonthSummary.policy.overLimitNotice}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={leaveReason}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Trim to 500 characters if copy-paste exceeds limit
                        if (value.length > 500) {
                          setLeaveReason(value.substring(0, 500));
                        } else {
                          setLeaveReason(value);
                        }
                      }}
                      maxLength={500}
                      rows={4}
                      className={`w-full border-2 rounded-lg shadow-sm focus:ring-2 text-base px-4 py-3 transition-all duration-200 resize-none ${
                        leaveReason.length >= 500 
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                          : 'border-gray-300 focus:ring-purple-500 focus:border-purple-500'
                      }`}
                      placeholder="Please provide a reason for your leave..."
                      required
                    />
                    <p className={`mt-1.5 text-xs ${leaveReason.length >= 500 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                      {leaveReason.length}/500 characters
                    </p>
                  </div>

                  {/* Info Note */}
                  <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-purple-600" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-purple-800">
                          Important Information
                        </p>
                        <p className="mt-1 text-sm text-purple-700">
                          Leave requests require manager approval. Approved leaves will not affect your required working hours.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
        </EmployeeDashboardDetailSheet>
      )}

      {/* Attendance Correction Request Modal */}
      {showCorrectionModal && (
        <div className="fixed inset-0 z-[9999] grid place-items-center p-4 sm:p-6 overflow-y-auto animate-fade-in">
          <div
            className="fixed inset-0 bg-gray-900/70 backdrop-blur-md transition-opacity animate-fade-in"
            onClick={() => setShowCorrectionModal(false)}
          ></div>

          <div className="relative z-10 w-full max-w-xl bg-white rounded-2xl text-left overflow-hidden shadow-professional-lg transform transition-all duration-300 animate-scale-in flex flex-col max-h-[90vh]">
              <div className="bg-gradient-to-r from-red-600 via-red-600 to-orange-600 px-6 py-5 shadow-professional">
                <div className="flex items-center">
                  <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-white/20 backdrop-blur-md border-2 border-white/30">
                    <ExclamationTriangleIcon className="h-7 w-7 text-white" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-bold text-white">Attendance Correction</h3>
                    <p className="text-sm text-red-100 mt-0.5">
                      Request correction only for dates with no attendance record
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white px-6 py-6 space-y-4 flex-1 overflow-y-auto min-h-0">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
                  <div className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 bg-gray-50">
                    {correctionDate ? moment(correctionDate).format('MMMM DD, YYYY') : 'Select a date'}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Check-in Time</label>
                    <input
                      type="time"
                      value={correctionCheckInTime}
                      onChange={(e) => setCorrectionCheckInTime(e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 text-base px-4 py-3 transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Check-out Time</label>
                    <input
                      type="time"
                      value={correctionCheckOutTime}
                      onChange={(e) => setCorrectionCheckOutTime(e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 text-base px-4 py-3 transition-all duration-200"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={correctionReason}
                    onChange={(e) => setCorrectionReason(e.target.value)}
                    rows={3}
                    className="w-full border-2 border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 text-base px-4 py-3 transition-all duration-200 resize-none"
                    placeholder="Explain why this correction is needed..."
                  />
                </div>

                <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-r-lg text-sm text-red-700">
                  Use this only when the day has no attendance record. If you already checked in and forgot to check out, submit a checkout request instead.
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse gap-3 border-t border-gray-200 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleSubmitCorrectionRequest}
                  disabled={submittingCorrection || !correctionReason.trim()}
                  className="w-full sm:w-auto inline-flex justify-center items-center rounded-xl border border-transparent shadow-professional px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-base font-semibold text-white hover:from-red-700 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingCorrection ? 'Submitting...' : 'Submit Request'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCorrectionModal(false)}
                  disabled={submittingCorrection}
                  className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-xl border-2 border-gray-300 shadow-professional px-6 py-3 bg-white text-base font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-all duration-300"
                >
                  Cancel
                </button>
              </div>
            </div>
        </div>
      )}

      {/* Extra Hours Request Modal */}
      {showExtraHoursModal && (
        <EmployeeDashboardDetailSheet
          isOpen={showExtraHoursModal}
          title="Request Extra Hours"
          summary="Request additional working hours for a specific date"
          eyebrow="Quick action"
          icon={<PlusCircleIcon className="h-5 w-5 text-blue-600" />}
          iconClassName="bg-blue-50 text-blue-700 ring-blue-200"
          onClose={() => setShowExtraHoursModal(false)}
          footer={(
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowExtraHoursModal(false)}
                disabled={submittingExtraHours}
                className="inline-flex w-full justify-center rounded-xl border border-slate-200 bg-white px-6 py-3 text-base font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitExtraHours}
                disabled={submittingExtraHours || !extraHoursReason.trim() || extraHoursReason.length >= 500 || !extraHoursRequested || extraHoursRequested < 0.5}
                className="inline-flex w-full items-center justify-center rounded-xl border border-transparent bg-blue-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {submittingExtraHours ? (
                  <>
                    <svg className="-ml-1 mr-3 h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </button>
            </div>
          )}
        >
                <div className="space-y-5">
                  {/* Date */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <DatePicker
                      selected={extraHoursDate}
                      onChange={(date) => setExtraHoursDate(date)}
                      maxDate={new Date()} // Only allow today or past dates
                      className="w-full border-2 border-gray-300 rounded-xl shadow-professional focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base px-4 py-3 transition-all duration-300"
                      dateFormat="MMM dd, yyyy"
                      placeholderText="Select date"
                    />
                  </div>

                  {/* Extra Hours */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Extra Hours Requested <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={extraHoursRequested}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0.5) {
                          setExtraHoursRequested(value);
                        } else if (e.target.value === '') {
                          setExtraHoursRequested('');
                        }
                      }}
                      className="w-full border-2 border-gray-300 rounded-xl shadow-professional focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base px-4 py-3 transition-all duration-300"
                      placeholder="e.g., 1.5, 2, 2.5"
                      required
                    />
                    <p className="mt-1.5 text-xs text-gray-500">
                      Minimum 0.5 hours, decimals allowed (e.g., 1.5, 2.5)
                    </p>
                  </div>

                  {/* Optional Start & End Time */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Start Time (Optional)
                      </label>
                      <input
                        type="datetime-local"
                        value={extraHoursStartTime ? moment(extraHoursStartTime).format('YYYY-MM-DDTHH:mm') : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setExtraHoursStartTime(value ? moment(value).toDate() : null);
                        }}
                        className="w-full border-2 border-gray-300 rounded-xl shadow-professional focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base px-4 py-3 transition-all duration-300"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        End Time (Optional)
                      </label>
                      <input
                        type="datetime-local"
                        value={extraHoursEndTime ? moment(extraHoursEndTime).format('YYYY-MM-DDTHH:mm') : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setExtraHoursEndTime(value ? moment(value).toDate() : null);
                        }}
                        min={extraHoursStartTime ? moment(extraHoursStartTime).format('YYYY-MM-DDTHH:mm') : ''}
                        className="w-full border-2 border-gray-300 rounded-xl shadow-professional focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base px-4 py-3 transition-all duration-300"
                      />
                    </div>
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Reason / Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={extraHoursReason}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 500) {
                          setExtraHoursReason(value);
                        }
                      }}
                      maxLength={500}
                      rows={4}
                      className={`w-full border-2 rounded-lg shadow-sm focus:ring-2 text-base px-4 py-3 transition-all duration-200 resize-none ${
                        extraHoursReason.length >= 500 
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                          : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      placeholder="Please provide a reason for requesting extra hours..."
                      required
                    />
                    <p className={`mt-1.5 text-xs ${extraHoursReason.length >= 500 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                      {extraHoursReason.length}/500 characters
                    </p>
                  </div>

                  {/* Info Note */}
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-blue-800">
                          Important Information
                        </p>
                        <p className="mt-1 text-sm text-blue-700">
                          Your request will be sent to your reporting manager for approval. Approved extra hours will be added to your working hours for that day. Managers can approve partial hours if needed.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
        </EmployeeDashboardDetailSheet>
      )}

      {/* Edit Task Dates Modal */}
      {showEditTaskModal && editingTask && (
        <div className="fixed inset-0 z-[9999] grid place-items-center p-4 sm:p-6 overflow-y-auto">
          <div 
            className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm transition-opacity" 
            onClick={() => setShowEditTaskModal(false)}
          ></div>

          <div className="relative z-10 w-full max-w-2xl bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all flex flex-col max-h-[90vh]">
              {/* Header */}
              <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-white/20 backdrop-blur-md border-2 border-white/30">
                      <CalendarDaysIcon className="h-7 w-7 text-white" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-bold text-white">Edit Task Dates</h3>
                      <p className="text-sm text-teal-100 mt-0.5">Schedule your task</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowEditTaskModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="bg-white px-6 py-6 flex-1 overflow-y-auto min-h-0">
                <div className="space-y-6">
                  {/* Task Info */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Task Details</h4>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-base font-medium text-gray-900">{editingTask.name}</div>
                      {editingTask.url && (
                        <a
                          href={editingTask.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors hover:underline"
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          {workloadData?.teamId ? 'Open in ClickUp' : 'View Task'}
                        </a>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {editingTask.list && editingTask.list !== 'N/A' && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded font-medium">
                          {editingTask.list}
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded font-medium ${
                        editingTask.status?.toLowerCase().includes('progress') 
                          ? 'bg-yellow-100 text-yellow-800'
                          : editingTask.status?.toLowerCase().includes('complete')
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {editingTask.status || 'N/A'}
                      </span>
                      {editingTask.priority && editingTask.priority !== 'None' && editingTask.priority !== 'N/A' && (
                        <span className={`px-2 py-1 rounded font-medium ${
                          editingTask.priority.toLowerCase() === 'urgent' 
                            ? 'bg-red-100 text-red-800'
                            : editingTask.priority.toLowerCase() === 'high'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {editingTask.priority}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Date Fields */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Start Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={taskEditForm.start_date}
                        onChange={(e) => setTaskEditForm({ ...taskEditForm, start_date: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {editingTask.start_date 
                          ? `Current: ${moment(parseInt(editingTask.start_date)).format('MMM DD, YYYY')}` 
                          : 'Not currently set'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Due Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={taskEditForm.due_date}
                        onChange={(e) => setTaskEditForm({ ...taskEditForm, due_date: e.target.value })}
                        min={taskEditForm.start_date || ''}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {editingTask.due_date 
                          ? `Current: ${moment(parseInt(editingTask.due_date)).format('MMM DD, YYYY')}` 
                          : 'Not currently set'}
                      </p>
                    </div>
                  </div>

                  {/* Info Notice */}
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-blue-800">
                          Task Scheduling
                        </p>
                        <p className="mt-1 text-sm text-blue-700">
                          Setting dates will help you track and manage your tasks better. The task will appear in your timeline once dates are set.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse gap-3 border-t border-gray-200 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleSaveTask}
                  disabled={savingTask || !taskEditForm.start_date || !taskEditForm.due_date}
                  className="w-full sm:w-auto inline-flex justify-center items-center rounded-xl border border-transparent shadow-professional px-7 py-3.5 bg-gradient-to-r from-teal-600 to-teal-700 text-base font-semibold text-white hover:from-teal-700 hover:to-teal-800 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]"
                >
                  {savingTask ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Dates'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditTaskModal(false);
                    setEditingTask(null);
                    setTaskEditForm({ taskId: '', start_date: '', due_date: '' });
                  }}
                  disabled={savingTask}
                  className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-xl border-2 border-gray-300 shadow-professional px-7 py-3.5 bg-white text-base font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]"
                >
                  Cancel
                </button>
              </div>
          </div>
        </div>
      )}

	    </div>
    <UserSelfProfileModal
      isOpen={showProfileEdit}
      onClose={() => setShowProfileEdit(false)}
      user={user}
      onSaved={handleProfileSaved}
    />
    <UserProfilePhotoUploadModal
      isOpen={showProfilePhotoUpload}
      onClose={() => setShowProfilePhotoUpload(false)}
      user={user}
      onSaved={handleProfileSaved}
    />
    </>
  );
}

export default UserDashboard;
